import React, { useState, useEffect, useCallback, useRef } from 'react';
import { confirmar } from '../components/confirmacao';
import apiService from '../services/apiService';
import Dialogo from '../components/Dialogo';
import DateTimeInputBR, { isValidDateTime, toInitialDateTime } from '../components/DateTimeInputBR';
import { extractSubdomain, isQaEnvironment } from '../utils/subdomain';
import './PromptEditor.css';

// Ordena os prompts pela ordem do pipeline n8n, usando o número no início do `name`
// (ex: "2- Recorder Classification"); aceita decimal p/ passos intermediários
// (ex: "5.1- Feedback Longo" fica logo após "5- Feedback"). Sem número vão para o
// fim (alfabético). O `name` vem do banco (prompt_assignments.name). O slug NÃO é
// alterado — é usado nos workflows do n8n; muda só o rótulo exibido.
function promptStepNumber(item) {
    const m = /^\s*(\d+(?:\.\d+)?)\s*-/.exec(item && item.name ? item.name : '');
    return m ? parseFloat(m[1]) : Number.MAX_SAFE_INTEGER;
}
function orderPromptSlugs(arr) {
    return [...(arr || [])].sort((a, b) => {
        const d = promptStepNumber(a) - promptStepNumber(b);
        if (d !== 0) return d;
        return (a.name || a.slug).localeCompare(b.name || b.slug);
    });
}
function promptOptionLabel(item) {
    return item.name ? `${item.name} (${item.slug})` : item.slug;
}

// ===== TOAST =====
let _toastId = 0;
function useToasts() {
    const [toasts, setToasts] = useState([]);

    const addToast = useCallback((message, type = 'info') => {
        const id = ++_toastId;
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 4000);
    }, []);

    return { toasts, addToast };
}

function ToastContainer({ toasts }) {
    return (
        <div className="pe-toast-container">
            {toasts.map(t => (
                <div key={t.id} className={`pe-toast ${t.type}`}>
                    <span>{t.type === 'success' ? '✅' : t.type === 'error' ? '❌' : 'ℹ️'}</span>
                    {t.message}
                </div>
            ))}
        </div>
    );
}

// ===== ADD QUESTION MODAL =====
function AddQuestionModal({ onClose, onAdd }) {
    const [description, setDescription] = useState('');
    const [condition, setCondition] = useState('');
    const [scorePrompt, setScorePrompt] = useState('');
    const [category, setCategory] = useState('');
    const [weight, setWeight] = useState(1);
    const [earnPoints, setEarnPoints] = useState(false);
    const [points, setPoints] = useState('');

    const pointsValid = Number.isInteger(Number(points)) && String(points).trim() !== '';
    const canSubmit = description.trim() && scorePrompt.trim() && (!earnPoints || pointsValid);

    const handleSubmit = () => {
        if (!canSubmit) return;
        onAdd({
            description: description.trim(),
            condition: condition.trim() || null,
            score_prompt: scorePrompt.trim(),
            category: category.trim() || null,
            weight,
            earn_points: earnPoints,
            points: earnPoints ? parseInt(points, 10) : 0,
        });
        onClose();
    };

    return (
        <div className="pe-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="pe-modal">
                <div className="pe-modal-header">
                    <h3>Nova Pergunta</h3>
                    <button className="pe-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="pe-modal-body">
                    <div className="pe-form-group">
                        <label>Descrição *</label>
                        <textarea
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder="O que está sendo avaliado. Ex: O vendedor ofereceu o programa de fidelidade da loja."
                            rows={2}
                            autoFocus
                        />
                    </div>
                    <div className="pe-form-group">
                        <label>Condição de aplicabilidade</label>
                        <textarea
                            value={condition}
                            onChange={e => setCondition(e.target.value)}
                            placeholder='Quando a pergunta se aplica. Ex: "Aplica-se quando houve decisão de compra. Não se aplica se..." Vazio = sempre aplicável.'
                            rows={2}
                        />
                    </div>
                    <div className="pe-form-group">
                        <label>Critérios de nota (score_prompt) *</label>
                        <textarea
                            value={scorePrompt}
                            onChange={e => setScorePrompt(e.target.value)}
                            placeholder={'- 10: desempenho pleno...\n- 5: desempenho parcial...\n- 0: ausência/falha...'}
                            rows={4}
                        />
                    </div>
                    <div className="pe-form-group">
                        <label>Categoria</label>
                        <input
                            type="text"
                            value={category}
                            onChange={e => setCategory(e.target.value)}
                            placeholder="Ex: upsell, objeção..."
                        />
                    </div>
                    <div className="pe-form-group">
                        <label>Peso: {weight}</label>
                        <input
                            type="range"
                            min={1}
                            max={10}
                            value={weight}
                            onChange={e => setWeight(Number(e.target.value))}
                            style={{ width: '100%', accentColor: 'var(--primary-color)' }}
                        />
                    </div>
                    <div className="pe-form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={earnPoints}
                                onChange={e => setEarnPoints(e.target.checked)}
                                style={{ accentColor: 'var(--primary-color)' }}
                            />
                            Gera pontos
                        </label>
                        {earnPoints && (
                            <>
                                <input
                                    type="number"
                                    step={1}
                                    value={points}
                                    onChange={e => setPoints(e.target.value)}
                                    placeholder="Pontos *"
                                    title="Pontos a multiplicar pelo score (obrigatório)"
                                    style={{ marginTop: 6, width: '120px' }}
                                />
                                <span className="pe-label-hint" style={{ display: 'block', marginTop: 4 }}>
                                    Este número será multiplicado pelo score da pergunta, gerando a quantidade total de pontos ganhos nesta pergunta.
                                </span>
                            </>
                        )}
                    </div>
                </div>
                <div className="pe-modal-footer">
                    <button className="pe-btn pe-btn-secondary" onClick={onClose}>Cancelar</button>
                    <button className="pe-btn pe-btn-primary" onClick={handleSubmit} disabled={!canSubmit}>
                        Adicionar
                    </button>
                </div>
            </div>
        </div>
    );
}

// ===== QUESTIONS SUMMARY MODAL =====
function QuestionsSummaryModal({ original, current, onClose, onConfirm, loading }) {
    // Build change list
    const changes = [];

    // Find removed (in original but not in current)
    original.forEach(q => {
        const inCurrent = current.find(c => c._originalId === q._originalId && !c._isNew);
        if (!inCurrent || inCurrent._deleted) {
            changes.push({ type: 'removed', q });
        }
    });

    // Find added (new items)
    current.filter(c => c._isNew && !c._deleted).forEach(q => {
        changes.push({ type: 'added', q });
    });

    // Find modified (existing but changed)
    current.filter(c => !c._isNew && !c._deleted && c._modified).forEach(q => {
        const orig = original.find(o => o._originalId === q._originalId);
        const diffs = [];
        if (orig) {
            if (orig.description !== q.description) diffs.push({ field: 'Descrição', from: orig.description, to: q.description });
            if (orig.condition !== q.condition) diffs.push({ field: 'Condição', from: orig.condition || '(sempre aplicável)', to: q.condition || '(sempre aplicável)' });
            if (orig.score_prompt !== q.score_prompt) diffs.push({ field: 'Critérios de nota', from: orig.score_prompt, to: q.score_prompt });
            if (orig.category !== q.category) diffs.push({ field: 'Categoria', from: orig.category, to: q.category });
            if (orig.weight !== q.weight) diffs.push({ field: 'Peso', from: orig.weight, to: q.weight });
            if (!!orig.earn_points !== !!q.earn_points) diffs.push({ field: 'Gera pontos', from: orig.earn_points ? 'Sim' : 'Não', to: q.earn_points ? 'Sim' : 'Não' });
            if ((orig.points || 0) !== (q.points || 0)) diffs.push({ field: 'Pontos', from: orig.points || 0, to: q.points || 0 });
            if ((orig.ia_model || '') !== (q.ia_model || '')) diffs.push({ field: 'Modelo IA', from: orig.ia_model || '(herdado)', to: q.ia_model || '(herdado)' });
            if ((orig.effort || '') !== (q.effort || '')) diffs.push({ field: 'Effort', from: orig.effort || '(sem effort)', to: q.effort || '(sem effort)' });
        }
        changes.push({ type: 'modified', q, diffs });
    });

    const hasChanges = changes.length > 0;

    return (
        <div className="pe-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="pe-modal pe-modal-wide">
                <div className="pe-modal-header">
                    <h3>Resumo das Alterações</h3>
                    <button className="pe-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="pe-modal-body">
                    {!hasChanges ? (
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Nenhuma alteração detectada.</p>
                    ) : (
                        <div className="pe-summary-list">
                            {changes.map((change, idx) => (
                                <div
                                    key={idx}
                                    className={`pe-summary-item ${change.type === 'added' ? 'added' : change.type === 'removed' ? 'removed' : 'modified'}`}
                                >
                                    <div className="pe-summary-item-label">
                                        {change.type === 'added' ? '➕ Adicionada' : change.type === 'removed' ? '🗑️ Removida' : '✏️ Modificada'}
                                    </div>
                                    <div style={{ fontWeight: 500 }}>{change.q.description}</div>
                                    {change.type === 'modified' && change.diffs?.map((d, di) => (
                                        <span key={di} className="pe-summary-change">
                                            {d.field}: "{d.from}" → "{d.to}"
                                        </span>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="pe-modal-footer">
                    <button className="pe-btn pe-btn-secondary" onClick={onClose} disabled={loading}>Cancelar</button>
                    <button className="pe-btn pe-btn-success" onClick={onConfirm} disabled={!hasChanges || loading}>
                        {loading ? 'Enviando...' : 'Confirmar e Enviar'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ===== QUESTION CARD =====
// Níveis de effort que valem para a pergunta: os do modelo dela, senão os do herdado.
// Modelos do dropdown de um PROMPT: sem os que só valem por pergunta do 4-Eval (so_por_pergunta) e sem os que só
// valem em outros slugs (so_slugs — ex.: o Jev, só no awon_cut_boundary_context do recorte do AWON).
const modelosDoPrompt = (iaModels, slug) => iaModels.filter((m) => !m.so_por_pergunta
    && (!(m.so_slugs || []).length || m.so_slugs.includes(slug)));

function effortsDaPergunta(q, iaModels, herdadoKey) {
    const key = q.ia_model || herdadoKey;
    return (iaModels.find((m) => m.model_key === key)?.efforts) || [];
}

function QuestionCard({ q, index, behaviors = [], onChange, onDelete, ia = null }) {
    // `ia` só existe no 4-Eval (evaluation_prompt), o único que roda cada pergunta no modelo dela.
    const efforts = ia ? effortsDaPergunta(q, ia.models, ia.herdadoKey) : [];
    const modeloEfetivo = ia ? (q.ia_model || ia.herdadoKey) : null;
    const labelDe = (key) => (ia?.models.find((m) => m.model_key === key)?.label) || key;
    const effortInvalido = !!(ia && q.effort && !efforts.includes(q.effort));
    // effort que vale quando a pergunta não escolhe nenhum (ex.: gpt-6-luna = none)
    const effortPadrao = ia ? (ia.models.find((m) => m.model_key === modeloEfetivo)?.effort_padrao || null) : null;
    return (
        <div className={`pe-question-card ${q._modified ? 'modified' : ''}`}>
            <div className="pe-question-card-header">
                <span className="pe-question-number">#{index + 1}</span>
                {q._modified && (
                    <span style={{ fontSize: '0.72rem', color: '#f9a825', fontWeight: 600 }}>● Modificada</span>
                )}
                <button
                    className="pe-btn pe-btn-danger"
                    style={{ padding: '3px 10px', fontSize: '0.78rem', marginLeft: 'auto' }}
                    onClick={() => onDelete(q)}
                    title="Remover pergunta"
                >
                    🗑️ Remover
                </button>
            </div>
            <div className="pe-question-fields-v2">
                <div className="pe-question-text-field">
                    <label>Descrição</label>
                    <textarea
                        value={q.description}
                        onChange={e => onChange(q, 'description', e.target.value)}
                        rows={2}
                    />
                </div>
                <div className="pe-question-text-field">
                    <label>Condição de aplicabilidade <span className="pe-label-hint">(vazio = sempre aplicável)</span></label>
                    <textarea
                        value={q.condition}
                        onChange={e => onChange(q, 'condition', e.target.value)}
                        rows={2}
                    />
                </div>
                <div className="pe-question-text-field">
                    <label>Critérios de nota (score_prompt)</label>
                    <textarea
                        value={q.score_prompt}
                        onChange={e => onChange(q, 'score_prompt', e.target.value)}
                        rows={4}
                    />
                </div>
                <div className="pe-question-meta-row">
                    <div className="pe-question-meta">
                        <label>Categoria</label>
                        <input
                            type="text"
                            value={q.category || ''}
                            onChange={e => onChange(q, 'category', e.target.value)}
                            placeholder="categoria"
                        />
                    </div>
                    <div className="pe-weight-group">
                        <label>Peso</label>
                        <div className="pe-weight-row">
                            <input
                                type="range"
                                min={1}
                                max={10}
                                value={q.weight || 1}
                                onChange={e => onChange(q, 'weight', Number(e.target.value))}
                            />
                            <span className="pe-weight-value">{q.weight || 1}</span>
                        </div>
                    </div>
                </div>

                {/* Seção organizada de pontuação/comportamento */}
                <div className="pe-points-section">
                    <div className="pe-points-section-title">⭐ Configurações de Pontos</div>
                    <div className="pe-points-fields">
                        <label className="pe-points-check" title="Se marcado, esta pergunta gera pontos.">
                            <input
                                type="checkbox"
                                checked={!!q.earn_points}
                                onChange={e => onChange(q, 'earn_points', e.target.checked)}
                                style={{ accentColor: 'var(--primary-color)' }}
                            />
                            Gera pontos
                        </label>
                        <div className="pe-points-field">
                            <label>Pontos</label>
                            <input
                                type="number"
                                step={1}
                                value={q.earn_points ? (q.points ?? '') : ''}
                                disabled={!q.earn_points}
                                onChange={e => onChange(q, 'points', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                                placeholder="Pontos *"
                                title="Pontos a multiplicar pelo score (obrigatório quando gera pontos)"
                            />
                        </div>
                        <div className="pe-points-field pe-points-field-grow">
                            <label>Comportamento</label>
                            <select
                                value={q.behavior_key || ''}
                                onChange={e => onChange(q, 'behavior_key', e.target.value)}
                                title="Agrupa os pontos desta pergunta. Vazio = não pontua agrupado por comportamento."
                            >
                                <option value="">— sem comportamento —</option>
                                {behaviors.map(b => (
                                    <option key={b.behavior_key} value={b.behavior_key}>
                                        {b.display_name || b.behavior_key}
                                    </option>
                                ))}
                                {q.behavior_key && !behaviors.some(b => b.behavior_key === q.behavior_key) && (
                                    <option value={q.behavior_key}>{q.behavior_key} (removido)</option>
                                )}
                            </select>
                        </div>
                    </div>
                    {q.earn_points && (
                        <span className="pe-label-hint pe-points-hint">
                            O valor de <strong>Pontos</strong> é multiplicado pelo score da pergunta = pontos ganhos.
                            O <strong>Comportamento</strong> agrupa esses pontos no resultado.
                        </span>
                    )}
                </div>

                {ia && (
                    <div className="pe-points-section">
                        <div className="pe-points-section-title">🤖 Modelo de IA desta pergunta</div>
                        <div className="pe-points-fields">
                            <div className="pe-points-field pe-points-field-grow">
                                <label>Modelo</label>
                                <select
                                    value={q.ia_model || ''}
                                    onChange={(e) => {
                                        const novo = e.target.value;
                                        onChange(q, 'ia_model', novo);
                                        // effort que o modelo novo não aceita sai junto, em vez de virar erro ao salvar
                                        const aceitos = effortsDaPergunta({ ia_model: novo }, ia.models, ia.herdadoKey);
                                        if (q.effort && !aceitos.includes(q.effort)) onChange(q, 'effort', '');
                                    }}
                                    title="Vazio = usa o modelo do prompt (ou o padrão da empresa)."
                                >
                                    <option value="">Herdar ({labelDe(ia.herdadoKey) || 'padrão da empresa'})</option>
                                    {ia.models.filter((m) => !(m.so_slugs || []).length).map((m) => (
                                        <option key={m.model_key} value={m.model_key}>{m.label}</option>
                                    ))}
                                    {q.ia_model && !ia.models.some((m) => m.model_key === q.ia_model) && (
                                        <option value={q.ia_model}>{q.ia_model} (desabilitado)</option>
                                    )}
                                </select>
                            </div>
                            <div className="pe-points-field">
                                <label>Effort</label>
                                <select
                                    value={q.effort || ''}
                                    onChange={(e) => onChange(q, 'effort', e.target.value)}
                                    disabled={efforts.length === 0 && !q.effort}
                                    title={efforts.length ? 'Profundidade de raciocínio desta pergunta. Vazio = sem effort, como sempre foi.'
                                        : `O modelo ${labelDe(modeloEfetivo)} não aceita effort.`}
                                >
                                    <option value="">{effortPadrao ? `Padrão do modelo (${effortPadrao})` : 'Padrão (sem effort)'}</option>
                                    {efforts.map((e) => <option key={e} value={e}>{e}</option>)}
                                    {effortInvalido && <option value={q.effort}>{q.effort} (inválido)</option>}
                                </select>
                            </div>
                        </div>
                        {effortInvalido && (
                            <span className="pe-label-hint pe-points-hint" style={{ color: '#c62828' }}>
                                O effort <strong>{q.effort}</strong> não vale para {labelDe(modeloEfetivo)}: escolha outro nível ou outro modelo.
                            </span>
                        )}
                        {!effortInvalido && q._effortAviso && (
                            <span className="pe-label-hint pe-points-hint" style={{ color: '#f9a825' }}>{q._effortAviso}</span>
                        )}
                        {efforts.length === 0 && !q.effort && (
                            <span className="pe-label-hint pe-points-hint">
                                {labelDe(modeloEfetivo)} não aceita effort. Para usar, escolha um modelo que aceite.
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// ===== PLAYBOOK MODAL =====
function PlaybookModal({ subdomain, onClose, onDone, addToast }) {
    const [refCompanies, setRefCompanies] = useState([]);
    const [reference, setReference] = useState('leroymerlin');
    const [file, setFile] = useState(null);
    const [job, setJob] = useState(null);
    const pollRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const data = await apiService.promptEditorGetCompaniesWithPrompts();
                const list = (data.companies || []).filter(c => c.default_prompts > 0 && c.subdomain !== subdomain);
                setRefCompanies(list);
                if (!list.find(c => c.subdomain === 'leroymerlin') && list.length > 0) {
                    setReference(list[0].subdomain);
                }
            } catch (err) {
                addToast(err.message, 'error');
            }
        })();
        return () => pollRef.current && clearInterval(pollRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startPolling = (jobId) => {
        pollRef.current = setInterval(async () => {
            try {
                const data = await apiService.promptEditorGetPlaybookJob(jobId);
                setJob(data.job);
                if (data.job.status === 'done' || data.job.status === 'error') {
                    clearInterval(pollRef.current);
                    if (data.job.status === 'done') {
                        addToast('Playbook processado: prompts gerados com sucesso!', 'success');
                        onDone();
                    } else {
                        addToast(`Falha no processamento do playbook: ${data.job.error}`, 'error');
                    }
                }
            } catch (_e) { /* mantém o polling em erros transitórios */ }
        }, 5000);
    };

    const handleSubmit = async () => {
        if (!file) return;
        try {
            setJob({ status: 'queued' });
            const resp = await apiService.promptEditorUploadPlaybook({ file, subdomain, referenceSubdomain: reference });
            startPolling(resp.job_id);
        } catch (err) {
            setJob(null);
            addToast(err.message, 'error');
        }
    };

    const inProgress = job && (job.status === 'queued' || job.status === 'running');
    const progress = job?.progress;

    return (
        <div className="pe-modal-overlay" onClick={e => e.target === e.currentTarget && !inProgress && onClose()}>
            <div className="pe-modal pe-modal-wide">
                <div className="pe-modal-header">
                    <h3>📚 Criar Prompts pelo Playbook — {subdomain}</h3>
                    <button className="pe-modal-close" onClick={onClose} disabled={inProgress}>×</button>
                </div>
                <div className="pe-modal-body">
                    {!job && (
                        <>
                            <div className="pe-danger-box">
                                ⚠️ <strong>Atenção:</strong> este processo vai <strong>desativar TODOS os prompts atuais
                                da empresa "{subdomain}"</strong> (incluindo os de lojas) e gerar prompts novos para todos
                                os slugs default a partir do documento enviado. A seleção de loja é ignorada — apenas
                                prompts default são criados.
                            </div>
                            <div className="pe-form-group">
                                <label>Documento do playbook (.pdf, .docx, .xlsx, .txt) *</label>
                                <input
                                    type="file"
                                    accept=".pdf,.docx,.xlsx,.xlsm,.txt,.md,.csv"
                                    onChange={e => setFile(e.target.files?.[0] || null)}
                                />
                            </div>
                            <div className="pe-form-group">
                                <label>Empresa referência (estrutura dos prompts)</label>
                                <select value={reference} onChange={e => setReference(e.target.value)}>
                                    {refCompanies.map(c => (
                                        <option key={c.subdomain} value={c.subdomain}>
                                            {c.name} ({c.default_prompts} prompts)
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}
                    {inProgress && (
                        <div className="pe-job-progress">
                            <div className="pe-spinner" />
                            {progress?.phase === 'generating' ? (
                                <p>Gerando prompts ({progress.done + 1}/{progress.total})… <br />
                                    <strong>{progress.current_slug}</strong></p>
                            ) : progress?.phase === 'writing' ? (
                                <p>Gravando prompts no banco…</p>
                            ) : (
                                <p>Analisando o documento…</p>
                            )}
                            <small>Esse processo leva alguns minutos. Não feche esta janela.</small>
                        </div>
                    )}
                    {job?.status === 'done' && (
                        <div className="pe-job-result">
                            <p>✅ <strong>{job.result.created.length} prompts gerados</strong> a partir do playbook
                                (referência: {job.result.reference}).</p>
                            <ul>
                                {job.result.created.map(c => (
                                    <li key={c.slug}>{c.slug}{c.questions ? ` — ${c.questions} perguntas` : ''}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {job?.status === 'error' && (
                        <div className="pe-danger-box">❌ {job.error}</div>
                    )}
                </div>
                <div className="pe-modal-footer">
                    {!job && (
                        <>
                            <button className="pe-btn pe-btn-secondary" onClick={onClose}>Cancelar</button>
                            <button className="pe-btn pe-btn-danger" onClick={handleSubmit} disabled={!file}>
                                Substituir todos os prompts
                            </button>
                        </>
                    )}
                    {(job?.status === 'done' || job?.status === 'error') && (
                        <button className="pe-btn pe-btn-primary" onClick={onClose}>Fechar</button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ===== IMPORT PLAYBOOK TEXT MODAL =====
function ImportPlaybookTextModal({ subdomain, storeId, storeName, onClose, onDone, addToast }) {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const scopeLabel = storeId ? `loja "${storeName || storeId}"` : `empresa "${subdomain}" (global)`;

    const handleSubmit = async () => {
        if (!file) return;
        setLoading(true);
        try {
            await apiService.promptEditorImportPlaybookText({ file, subdomain, storeId });
            addToast('Texto do playbook importado com sucesso!', 'success');
            onDone();
            onClose();
        } catch (err) {
            addToast(err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="pe-modal-overlay" onClick={e => e.target === e.currentTarget && !loading && onClose()}>
            <div className="pe-modal">
                <div className="pe-modal-header">
                    <h3>📥 Importar Texto do Playbook — {subdomain}</h3>
                    <button className="pe-modal-close" onClick={onClose} disabled={loading}>×</button>
                </div>
                <div className="pe-modal-body">
                    <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
                        Importa o <strong>texto</strong> do arquivo enviado como o playbook (slug <code>playbook</code>)
                        no escopo da <strong>{scopeLabel}</strong>. Diferente do
                        {' '}<strong>"Criar Prompts pelo Playbook"</strong>, este processo <strong>não apaga nem gera</strong>{' '}
                        outros prompts — apenas grava/atualiza o texto do playbook.
                    </p>
                    <p className="pe-slug-hint" style={{ margin: '8px 0 14px' }}>
                        ℹ️ O playbook importado é usado apenas no prompt <strong>"5.1- Feedback Longo"</strong>.
                    </p>
                    <div className="pe-form-group">
                        <label>Documento do playbook (.pdf, .docx, .xlsx, .txt) *</label>
                        <input
                            type="file"
                            accept=".pdf,.docx,.xlsx,.xlsm,.txt,.md,.csv"
                            onChange={e => setFile(e.target.files?.[0] || null)}
                            disabled={loading}
                        />
                    </div>
                </div>
                <div className="pe-modal-footer">
                    <button className="pe-btn pe-btn-secondary" onClick={onClose} disabled={loading}>Cancelar</button>
                    <button className="pe-btn pe-btn-primary" onClick={handleSubmit} disabled={loading || !file}>
                        {loading ? 'Importando…' : 'Importar'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ===== DEFAULT PROMPTS MODAL =====
function DefaultPromptsModal({ subdomain, onClose, onDone, addToast }) {
    const [sources, setSources] = useState([]);
    const [source, setSource] = useState('leroymerlin');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const data = await apiService.promptEditorGetCompaniesWithPrompts();
                const list = (data.companies || []).filter(c => c.default_prompts > 0 && c.subdomain !== subdomain);
                setSources(list);
                if (!list.find(c => c.subdomain === 'leroymerlin') && list.length > 0) {
                    setSource(list[0].subdomain);
                }
            } catch (err) {
                addToast(err.message, 'error');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const resp = await apiService.promptEditorCopyDefaultPrompts({
                source_subdomain: source,
                target_subdomain: subdomain,
            });
            setResult(resp);
            addToast('Prompts padrão criados com sucesso!', 'success');
            onDone();
        } catch (err) {
            addToast(err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="pe-modal-overlay" onClick={e => e.target === e.currentTarget && !loading && onClose()}>
            <div className="pe-modal">
                <div className="pe-modal-header">
                    <h3>🧬 Gerar Prompts Padrão — {subdomain}</h3>
                    <button className="pe-modal-close" onClick={onClose} disabled={loading}>×</button>
                </div>
                <div className="pe-modal-body">
                    {!result ? (
                        <>
                            <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
                                Copia os prompts default de uma empresa existente para preencher todos os slugs
                                de <strong>{subdomain}</strong>. Disponível apenas para empresas que ainda não têm prompts.
                            </p>
                            <div className="pe-form-group">
                                <label>Copiar prompts default de:</label>
                                <select value={source} onChange={e => setSource(e.target.value)}>
                                    {sources.map(c => (
                                        <option key={c.subdomain} value={c.subdomain}>
                                            {c.name} ({c.default_prompts} prompts)
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    ) : (
                        <div className="pe-job-result">
                            <p>✅ <strong>{result.created.length} prompts copiados</strong> de {result.source}.</p>
                            <ul>
                                {result.created.map(c => (
                                    <li key={c.slug}>{c.slug}{c.questions ? ` — ${c.questions} perguntas` : ''}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                <div className="pe-modal-footer">
                    {!result ? (
                        <>
                            <button className="pe-btn pe-btn-secondary" onClick={onClose} disabled={loading}>Cancelar</button>
                            <button className="pe-btn pe-btn-primary" onClick={handleSubmit} disabled={loading || !source}>
                                {loading ? 'Copiando…' : 'Criar prompts padrão'}
                            </button>
                        </>
                    ) : (
                        <button className="pe-btn pe-btn-primary" onClick={onClose}>Fechar</button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ===== COPY STORE PROMPTS MODAL =====
function CopyStorePromptsModal({ subdomain, stores, targetStoreId, onClose, onDone, addToast }) {
    const targetStore = stores.find(s => String(s.id) === String(targetStoreId));
    const sourceOptions = stores.filter(s => String(s.id) !== String(targetStoreId));
    const [sourceStoreId, setSourceStoreId] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const resp = await apiService.promptEditorCopyStorePrompts({
                subdomain,
                source_store_id: Number(sourceStoreId),
                target_store_id: Number(targetStoreId),
            });
            setResult(resp);
            addToast('Prompts copiados com sucesso!', 'success');
            onDone();
        } catch (err) {
            addToast(err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const sourceName = sourceOptions.find(s => String(s.id) === String(sourceStoreId))?.display_name;

    return (
        <div className="pe-modal-overlay" onClick={e => e.target === e.currentTarget && !loading && onClose()}>
            <div className="pe-modal">
                <div className="pe-modal-header">
                    <h3>📋 Copiar Prompts de Outra Loja</h3>
                    <button className="pe-modal-close" onClick={onClose} disabled={loading}>×</button>
                </div>
                <div className="pe-modal-body">
                    {!result ? (
                        <>
                            <div className="pe-danger-box">
                                ⚠️ <strong>Atenção:</strong> todos os prompts atuais da loja{' '}
                                <strong>{targetStore?.display_name || targetStoreId}</strong> serão desativados e
                                passarão a valer os prompts da loja escolhida abaixo (cópia independente — edições
                                futuras em uma loja não afetam a outra).
                            </div>
                            <div className="pe-form-group">
                                <label>Copiar prompts da loja:</label>
                                <select value={sourceStoreId} onChange={e => setSourceStoreId(e.target.value)}>
                                    <option value="">— Selecione a loja origem —</option>
                                    {sourceOptions.map(s => (
                                        <option key={s.id} value={s.id}>{s.display_name}</option>
                                    ))}
                                </select>
                            </div>
                        </>
                    ) : (
                        <div className="pe-job-result">
                            <p>✅ <strong>{result.created.length} prompt(s) copiado(s)</strong> de {sourceName} para{' '}
                                {targetStore?.display_name}
                                {result.deactivated_assignments > 0 && ` (${result.deactivated_assignments} assignment(s) anteriores desativados)`}.</p>
                            <ul>
                                {result.created.map(c => (
                                    <li key={c.slug}>{c.slug}{c.questions ? ` — ${c.questions} perguntas` : ''}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                <div className="pe-modal-footer">
                    {!result ? (
                        <>
                            <button className="pe-btn pe-btn-secondary" onClick={onClose} disabled={loading}>Cancelar</button>
                            <button className="pe-btn pe-btn-danger" onClick={handleSubmit} disabled={loading || !sourceStoreId}>
                                {loading ? 'Copiando…' : 'Substituir prompts da loja'}
                            </button>
                        </>
                    ) : (
                        <button className="pe-btn pe-btn-primary" onClick={onClose}>Fechar</button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ===== HELPER: normalize questions from API =====
function normalizeQuestions(apiQuestions) {
    return (apiQuestions || []).map(q => ({
        _key: q.id,
        _originalId: q.original_id || q.id,
        _currentId: q.id,
        _isNew: false,
        _modified: false,
        _deleted: false,
        // perguntas não migradas para o modelo v2 exibem o content legado como descrição
        description: q.description || q.content || '',
        condition: q.condition || '',
        score_prompt: q.score_prompt || '',
        category: q.category || '',
        weight: q.weight || 1,
        earn_points: !!q.earn_points,
        points: q.points || 0,
        behavior_key: q.behavior_key || '',
        // override por pergunta (só o 4-Eval usa); vazio = herda / sem effort
        ia_model: q.ia_model || '',
        effort: q.effort || '',
        _effortAviso: q.effort_aviso || null,
    }));
}

let _newQuestionKey = -1;

// ===== PROMPT TEST RUNNER =====
function PromptTestRunner({ subdomain, slug, promptId, storeId, addToast }) {
    const hostSubdomain = extractSubdomain();
    const isAdminQa = hostSubdomain === 'admin' && isQaEnvironment();
    const [dataSize, setDataSize] = useState(6);
    const [newDataset, setNewDataset] = useState(false);
    const [running, setRunning] = useState(false);
    const pollRef = useRef(null);

    useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

    const startStatusPolling = (runId) => {
        pollRef.current = setInterval(async () => {
            try {
                const data = await apiService.promptEditorGetTestStatus(runId);
                if (data.status === 'success') {
                    clearInterval(pollRef.current);
                    setRunning(false);
                    addToast('✅ Teste de prompt concluído!', 'success');
                } else if (data.status === 'error') {
                    clearInterval(pollRef.current);
                    setRunning(false);
                    addToast('❌ O teste de prompt falhou no n8n. Verifique a execução.', 'error');
                } else if (data.status === 'timeout') {
                    clearInterval(pollRef.current);
                    setRunning(false);
                    addToast('⏱️ O teste não finalizou em 60 minutos — verifique no n8n.', 'error');
                }
            } catch (_e) { /* erros transitórios não interrompem o polling */ }
        }, 10000);
    };

    const handleStartTest = async () => {
        if (!subdomain || !slug || !promptId) {
            addToast('Selecione subdomain, slug e um prompt antes de iniciar o teste.', 'error');
            return;
        }
        setRunning(true);
        try {
            const resp = await apiService.promptEditorStartTest({
                subdomain,
                data_size: Number(dataSize),
                slug,
                prompt_id: promptId,
                new_dataset: newDataset,
                store_id: storeId,
            });
            addToast('Teste de prompt iniciado! Acompanhando a execução…', 'success');
            if (resp?.test_run_id) {
                startStatusPolling(resp.test_run_id);
            } else {
                setRunning(false);
            }
        } catch (err) {
            addToast(`Erro ao iniciar teste de prompt: ${err.message}`, 'error');
            setRunning(false);
        }
    };

    if (!isAdminQa) return null;

    return (
        <div className="pe-info-bar" style={{ justifyContent: 'flex-end', gap: 8 }}>
            <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Tamanho da amostra:
            </label>
            <select
                value={dataSize}
                onChange={e => setDataSize(Number(e.target.value))}
                disabled={running}
                style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: '1px solid var(--border-color, #ccc)',
                    background: 'var(--bg-secondary, #fff)',
                    color: 'var(--text-primary, #000)',
                }}
            >
                <option value={2}>2</option>
                <option value={6}>6</option>
                <option value={12}>12</option>
                <option value={30}>30</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
            </select>
            <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Criar novo dataset?
            </label>
            <select
                value={newDataset ? 'yes' : 'no'}
                onChange={e => setNewDataset(e.target.value === 'yes')}
                disabled={running}
                style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: '1px solid var(--border-color, #ccc)',
                    background: 'var(--bg-secondary, #fff)',
                    color: 'var(--text-primary, #000)',
                }}
            >
                <option value="no">Não</option>
                <option value="yes">Sim</option>
            </select>
            <button
                className="pe-btn pe-btn-blue"
                onClick={handleStartTest}
                disabled={running || !promptId}
            >
                {running ? '⏳ Teste em andamento…' : '🧪 Iniciar Teste de Prompt'}
            </button>
            <a
                className="pe-btn"
                href="https://docs.google.com/spreadsheets/d/1RhUlJInatZgvv7UhHg5_brg0OI3wApnbxmzQZCj1aNo/edit?gid=1272223637#gid=1272223637"
                target="_blank"
                rel="noopener noreferrer"
                title="Abrir a Planilha de Resultados dos testes (Google Sheets)"
            >
                📊 Planilha de Resultados
            </a>
        </div>
    );
}

// ===== Badge de origem do prompt (default global x específico da loja) =====
// onUseGlobal (opcional): habilita o botão que apaga o override e faz a loja
// voltar ao prompt global. Só aparece quando o prompt é próprio da loja.
function PromptOriginBadge({ resolvedData, storeId, onUseGlobal, usingGlobalBusy }) {
    if (!storeId || !resolvedData) return null;
    const sid = resolvedData.assignment_store_id;
    const specific = sid != null && String(sid) === String(storeId);
    return (
        <div className={`pe-origin-badge ${specific ? 'pe-origin-specific' : 'pe-origin-default'}`}>
            <span>
                {specific
                    ? '🏷️ Prompt específico desta loja'
                    : '🌐 Usando o prompt default (global) — esta loja não tem prompt próprio'}
            </span>
            {specific && onUseGlobal && (
                <button
                    className="pe-btn pe-btn-danger pe-origin-use-global"
                    onClick={onUseGlobal}
                    disabled={usingGlobalBusy}
                    title="Apaga o prompt próprio desta loja e faz ela passar a usar o prompt global"
                >
                    {usingGlobalBusy ? 'Aplicando...' : '🌐 Usar prompt GLOBAL'}
                </button>
            )}
        </div>
    );
}

// ===== Confirmação de "Usar prompt GLOBAL" =====
function UseGlobalPromptModal({ storeName, storeId, slugLabel, hasQuestions, onClose, onConfirm, loading }) {
    return (
        <div className="pe-modal-overlay" onClick={e => e.target === e.currentTarget && !loading && onClose()}>
            <div className="pe-modal">
                <div className="pe-modal-header">
                    <h3>🌐 Usar prompt global nesta loja</h3>
                    <button className="pe-modal-close" onClick={onClose} disabled={loading}>×</button>
                </div>
                <div className="pe-modal-body">
                    <p className="pe-ug-lead">
                        A loja <strong>{storeName || `#${storeId}`}</strong> passará a usar o prompt
                        global (default da empresa) em <strong>{slugLabel}</strong>.
                    </p>
                    <ul className="pe-ug-list">
                        <li>O prompt próprio desta loja deixa de ser usado.</li>
                        {hasQuestions && (
                            <li>As perguntas e a pontuação passam a ser as do global — as próprias da loja saem de uso.</li>
                        )}
                        <li>As outras lojas não são afetadas.</li>
                        <li>Salvar um prompt com esta loja selecionada recria o prompt próprio dela.</li>
                    </ul>
                </div>
                <div className="pe-modal-footer">
                    <button className="pe-btn pe-btn-secondary" onClick={onClose} disabled={loading}>Cancelar</button>
                    <button className="pe-btn pe-btn-danger" onClick={onConfirm} disabled={loading}>
                        {loading ? 'Aplicando...' : '🌐 Usar prompt GLOBAL'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Faz a loja selecionada voltar a usar o prompt global (apaga o override).
// Usado pelos dois editores: `run` abre o modal, `modal` é o JSX a renderizar.
function useUseGlobalPrompt({ selectors, addToast, onRefresh, hasQuestions }) {
    const [busy, setBusy] = useState(false);
    const [confirming, setConfirming] = useState(false);

    const confirm = async () => {
        setBusy(true);
        try {
            await apiService.promptEditorDeletePromptAssignment({
                subdomain: selectors.subdomain,
                slug: selectors.slug,
                store_id: selectors.storeId,
                phase_id: selectors.phaseId,
            });
            setConfirming(false);
            addToast('Loja agora usa o prompt global.', 'success');
            onRefresh();
        } catch (err) {
            addToast(err.message, 'error');
        } finally {
            setBusy(false);
        }
    };

    const modal = confirming ? (
        <UseGlobalPromptModal
            storeName={selectors.storeName}
            storeId={selectors.storeId}
            slugLabel={selectors.slugLabel || selectors.slug}
            hasQuestions={hasQuestions}
            loading={busy}
            onClose={() => setConfirming(false)}
            onConfirm={confirm}
        />
    ) : null;

    return { busy, run: () => setConfirming(true), modal };
}

// ===== PLACAR ABRE EM (unlock_date) =====
// O campo voltou para DENTRO deste bloco, e nao para a tela de Empresa/Loja, porque e' o
// interruptor da regua que o bloco configura: sem data, o `5- Feedback` nao entra no ramo de
// gamificacao (`usar_good_bad` do no `Get app_version`) e nao sai nem good/bad nem ponto de
// comportamento -- a regua fica cadastrada e inerte. Ele saiu da tela de empresa na §2.7 do
// plano de Fases porque aquecimento e' uma FASE; a coluna continuou existindo e continuou
// vencendo para quem nao esta em programa, que e' exatamente o ongoing.
//
// Nao entrou no editor de prompt de proposito, pelo mesmo motivo que tirou este painel de la
// (ver o comentario do <PointsConfigPanel>): a data nao depende de qual prompt esta escolhido.
//
// A CASCATA E' loja > empresa, e e' ela que decide o que o campo mostra em cada escopo:
//   ongoing -> core.companies.unlock_date   (PUT /api/company)
//   loja    -> <tenant>.stores.unlock_date  (PUT /api/store); vazio = herda a empresa
//   fase    -> a coluna NAO tem nivel de fase: quem decide e' `locks_scoreboard` e a data e'
//              calculada por `scoreboard_open_on()`. Mesmo movimento do WhitelabelConfig com
//              escopo de loja: em vez de um campo que nao gravaria nada, a tela diz onde e'.
//
// A PEGADINHA que precisa estar ESCRITA na tela: vazio nao e' "destravado", e' calibracao (o app
// faz `today >= unlock_date` em DynamicScreen.tsx:329 e sem a chave cai no estado de calibracao).
// E preencher aqui nao liga so o good/bad do feedback: ABRE placar e ranking no app.
function PlacarAbreEm({ subdomain, storeId, phaseId, addToast }) {
    const [companyId, setCompanyId] = useState(null);
    const [valor, setValor] = useState('');
    const [salvo, setSalvo] = useState('');       // o que esta no banco NESTE escopo
    const [heranca, setHeranca] = useState('');   // valor da empresa, p/ mostrar no escopo de loja
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!subdomain || phaseId) return;
        setLoading(true);
        try {
            // Mesma fonte que alimenta a tela de Empresa/Loja: ja devolve company_id, store_id e
            // o unlock_date dos dois niveis, entao nao precisa de endpoint novo.
            const d = await apiService.listStoresByUser();
            const emp = (d?.companies || []).find((c) => c.subdomain === subdomain);
            setCompanyId(emp?.company_id ?? null);
            const daEmpresa = toInitialDateTime(emp?.unlock_date);
            setHeranca(daEmpresa);
            if (storeId) {
                const lojas = (emp?.units || []).flatMap((u) => u.stores || []);
                const loja = lojas.find((x) => Number(x.store_id) === Number(storeId));
                const daLoja = toInitialDateTime(loja?.unlock_date);
                setSalvo(daLoja);
                setValor(daLoja);
            } else {
                setSalvo(daEmpresa);
                setValor(daEmpresa);
            }
        } catch (e) {
            addToast && addToast('Nao foi possivel carregar a data do placar', 'error');
        } finally {
            setLoading(false);
        }
    }, [subdomain, storeId, phaseId, addToast]);

    useEffect(() => { load(); }, [load]);

    const salvar = async () => {
        const v = (valor || '').trim();
        if (v && !isValidDateTime(v)) {
            addToast && addToast("Data invalida. Use 'AAAA-MM-DD HH:MM:SS'", 'error');
            return;
        }
        if (!companyId) {
            addToast && addToast('Empresa nao identificada; recarregue a pagina', 'error');
            return;
        }
        setSaving(true);
        try {
            // Vazio grava NULL (`clear_unlock_date`), que na loja significa voltar a herdar a
            // empresa e na empresa significa calibracao -- por isso os avisos abaixo.
            const corpo = v ? { unlock_date: v } : { clear_unlock_date: true };
            const r = storeId
                ? await apiService.updateStore({ company_id: companyId, store_id: Number(storeId), ...corpo })
                : await apiService.updateCompany({ company_id: companyId, ...corpo });
            if (r && r.success === false) throw new Error(r.message || 'Erro ao salvar');
            addToast && addToast(
                v ? 'Data do placar salva'
                  : storeId ? 'Loja volta a herdar a data da empresa'
                            : 'Data removida: a empresa volta para calibracao',
                'success');
            await load();
        } catch (e) {
            addToast && addToast(e.message || 'Erro ao salvar a data do placar', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (phaseId) {
        return (
            <div className="pe-unlock">
                <h4>🏁 Placar abre em — fase selecionada</h4>
                <p className="pe-pc-note">
                    Em fase esta data <strong>nao se configura aqui</strong>: quem decide e' a fase que
                    trava o placar (<code>locks_scoreboard</code>), e o valor e' calculado por vendedor —
                    a entrada da primeira fase que <strong>nao</strong> trava. Para mexer, use o programa
                    da fase em <strong>Programas</strong>. O campo abaixo aparece no escopo{' '}
                    <strong>Padrao da empresa (ongoing)</strong> e no de <strong>loja</strong>, que sao os
                    dois niveis que a coluna tem.
                </p>
            </div>
        );
    }

    const sujo = (valor || '').trim() !== (salvo || '').trim();
    const herdando = !!storeId && !(salvo || '').trim();

    return (
        <div className="pe-unlock">
            <h4>🏁 Placar abre em — {storeId ? 'loja selecionada' : 'ongoing (default da empresa)'}</h4>
            <p className="pe-pc-note">
                A partir desta data o atendimento passa a receber <strong>good/bad e pontuacao de
                comportamentos</strong>, e o app <strong>abre placar e ranking</strong> para o vendedor.
                Atendimento gravado antes dela continua no formato antigo — a regra e{' '}
                <code>inicio do atendimento &gt;= esta data</code>.
            </p>
            {loading ? <p>Carregando…</p> : (
                <>
                    <DateTimeInputBR
                        id="pe-unlock-date"
                        value={valor}
                        onChange={setValor}
                        allowClear
                    />
                    <small className="pe-pc-note">
                        {herdando
                            ? (heranca
                                ? <>Esta loja <strong>herda a empresa</strong> ({heranca}). Preencha para sobrescrever so nesta loja.</>
                                : <>Esta loja herda a empresa, que esta <strong>vazia</strong> — ou seja, calibracao.</>)
                            : storeId
                                ? <>Data <strong>propria desta loja</strong>. Limpe (✕) para voltar a herdar a empresa{heranca ? ` (${heranca})` : ''}.</>
                                : <>Vale para a empresa toda, exceto nas lojas que tiverem data propria.</>}
                    </small>
                    <small className="pe-pc-note">
                        ⚠️ <strong>Vazio nao e "destravado"</strong>: sem data o app cai no estado de{' '}
                        <strong>calibracao</strong>, e o feedback sai no formato antigo (texto unico, sem
                        pontos).
                    </small>
                    <div className="pe-pc-actions">
                        <button className="pe-btn pe-btn-primary" onClick={salvar} disabled={saving || !sujo}>
                            {saving ? 'Salvando…' : storeId ? 'Salvar data da loja' : 'Salvar data do ongoing'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

// ===== PONTUAÇÃO (cascading: default da empresa / override por loja) =====
/**
 * Com `phaseId`, a gravação vai para `phase_points_config` (§5.2). A API RECUSA (409) régua
 * própria em fase que não trava o placar — e é correto: pontos de réguas diferentes não podem
 * competir no mesmo ranking (§N21). O detalhe do erro é mostrado no toast porque é ele que diz
 * ao CS o que fazer (marcar "travar placar" na fase).
 */
// Datas da vigência em America/Sao_Paulo (é o fuso que decide em que dia um atendimento conta).
const dataBR = (deslocaDias = 0) => {
    const [a, m, d] = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' }).split('-').map(Number);
    const x = new Date(Date.UTC(a, m - 1, d + deslocaDias));
    return x.toISOString().slice(0, 10);
};
const fmtDataBR = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
const ROTULO_METRICA = { valid_attendance: 'atendimento válido', invalid_attendance: 'atendimento não-válido', recording_hour: 'hora de gravação' };

function PointsConfigPanel({ subdomain, storeId, phaseId, addToast }) {
    const userId = (() => {
        try { return JSON.parse(localStorage.getItem('aliado_auth'))?.user?.user_id; } catch (e) { return null; }
    })();
    const [data, setData] = useState(null);
    const [va, setVa] = useState('');
    const [inv, setInv] = useState('');
    const [rh, setRh] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    // Vigência: a partir de quando o preço novo vale. Padrão amanhã (o que já foi contado hoje não é repreciado).
    const [vf, setVf] = useState(() => dataBR(1));

    const load = useCallback(async () => {
        if (!subdomain) return;
        setLoading(true);
        try {
            // Mesmo bug do painel de comportamentos: o save já ia para `phase_points_config` e a
            // leitura vinha do ongoing, então a régua da fase não aparecia. Na fase, lê a régua dela
            // e cai no HERDADO quando a fase não sobrescreve aquela métrica.
            if (phaseId) {
                const r = await apiService.phaseRulerList({ subdomain, phase_id: phaseId });
                const d2 = r?.data || {};
                setData({ ...d2, _fase: true });
                const porMetrica = (lista) => Object.fromEntries((lista || []).map((x) => [x.metric, x]));
                const propria = porMetrica(d2.points_config);
                const herdada = porMetrica(d2.points_config_inherited);
                const valor = (m) => (propria[m] ?? herdada[m] ?? {}).points_per_unit ?? 0;
                setVa(valor('valid_attendance'));
                setInv(valor('invalid_attendance'));
                setRh(valor('recording_hour'));
            } else {
                const d = await apiService.pointsGetConfig({ userId, subdomain, store_id: storeId || undefined });
                setData(d);
                const src = storeId ? (d.has_override ? d.store : d.effective) : d.default;
                setVa(src?.valid_attendance ?? 0);
                setInv(src?.invalid_attendance ?? 0);
                setRh(src?.recording_hour ?? 0);
            }
        } catch (e) {
            addToast && addToast('Erro ao carregar configuração de pontos', 'error');
        } finally { setLoading(false); }
    }, [subdomain, storeId, phaseId, userId, addToast]);

    useEffect(() => { load(); }, [load]);

    // Régua de classificação da empresa (§N27). Só leitura: o mapa é alterado por dev, com
    // vigência nova, porque trocar o balde de uma classificação reprecifica todo atendimento a
    // partir da vigência. Vem do banco de propósito — o texto fixo que ficava aqui descrevia a
    // regra de antes de 17/08 e passou meses errado.
    const [clsRules, setClsRules] = useState(null);
    useEffect(() => {
        if (!subdomain) { setClsRules(null); return; }
        let vivo = true;
        apiService.pointsClassificationRules({ userId, subdomain })
            .then((d) => { if (vivo) setClsRules(d); })
            .catch(() => { if (vivo) setClsRules(null); });
        return () => { vivo = false; };
    }, [subdomain, userId]);

    const save = async () => {
        // o campo `min` do calendário não impede digitar: a API também recusa, mas barra aqui com a mesma mensagem
        if (!phaseId && vf && vf < dataBR(1)) {
            addToast && addToast(`A pontuação só pode valer a partir de amanhã (${fmtDataBR(dataBR(1))}).`, 'error');
            return;
        }
        setSaving(true);
        try {
            const itens = [
                { metric: 'valid_attendance', points_per_unit: Number(va) || 0 },
                { metric: 'invalid_attendance', points_per_unit: Number(inv) || 0 },
                { metric: 'recording_hour', points_per_unit: Number(rh) || 0 },
            ];
            if (phaseId) {
                // A API de fase grava uma métrica por chamada (upsert por phase_id+metric).
                // A API recusa (409) trocar o valor de uma régua de fase que já pontuou atendimento: reprecifica o que
                // já foi contado. Pergunta uma vez e repete todas as métricas com a confirmação.
                let confirma = false;
                for (const it of itens) {
                    try {
                        await apiService.phasePointsConfigSave({ subdomain, phase_id: phaseId, ...it, confirm_retroactive: confirma || undefined });
                    } catch (e) {
                        const det = e.response?.data?.detail;
                        if (e.response?.status === 409 && typeof det === 'string' && det.includes('confirm_retroactive')
                            && (await confirmar(`${det}\n\nSalvar mesmo assim?`, { titulo: 'Mudança retroativa na pontuação', rotuloConfirmar: 'Salvar mesmo assim' }))) {
                            confirma = true;
                            await apiService.phasePointsConfigSave({ subdomain, phase_id: phaseId, ...it, confirm_retroactive: true });
                        } else { throw e; }
                    }
                }
            } else {
                const r = await apiService.pointsSaveConfig({
                    userId, subdomain, store_id: storeId || undefined, items: itens,
                    valid_from: vf || undefined,
                });
                const quando = r?.starts_today ? 'a partir de hoje' : `a partir de ${fmtDataBR(r?.valid_from)}`;
                addToast && addToast(
                    `${storeId ? 'Configuração da loja' : 'Configuração do ongoing'} salva, valendo ${quando}. `
                    + 'Os dias anteriores seguem com o valor da época.', 'success');
                load();
                setSaving(false);
                return;
            }
            addToast && addToast('Pontuação da fase salva', 'success');
            load();
        } catch (e) {
            addToast && addToast(e.response?.data?.detail || 'Erro ao salvar pontuação', 'error');
        } finally { setSaving(false); }
    };

    const useDefault = async () => {
        setSaving(true);
        try {
            if (phaseId) {
                // NUNCA `pointsResetConfig` aqui: com store_id nulo ele zera o DEFAULT DA EMPRESA.
                // Na fase, "voltar a herdar" é apagar as linhas próprias dela, uma a uma por id.
                const proprias = data?.points_config || [];
                if (!proprias.length) {
                    addToast && addToast('Esta fase já herda a régua da loja/global.', 'info');
                    setSaving(false);
                    return;
                }
                let confirmaRem = false;
                for (const linha of proprias) {
                    try {
                        await apiService.phasePointsConfigDelete({ subdomain, id: linha.id, confirm_retroactive: confirmaRem || undefined });
                    } catch (e) {
                        const det = e.response?.data?.detail;
                        if (e.response?.status === 409 && typeof det === 'string' && det.includes('confirm_retroactive')
                            && (await confirmar(`${det}\n\nRemover mesmo assim?`, { titulo: 'Mudança retroativa na pontuação', perigo: true, rotuloConfirmar: 'Remover mesmo assim' }))) {
                            confirmaRem = true;
                            await apiService.phasePointsConfigDelete({ subdomain, id: linha.id, confirm_retroactive: true });
                        } else { throw e; }
                    }
                }
                addToast && addToast('A fase voltou a herdar a régua da loja/global.', 'success');
                load();
                setSaving(false);
                return;
            }
            try {
                await apiService.pointsResetConfig({ userId, subdomain, store_id: storeId });
            } catch (e) {
                // Remover o override reprecifica o passado da loja: a API pede confirmação explícita.
                const det = e.response?.data?.detail;
                if (e.response?.status === 409 && typeof det === 'string'
                    && (await confirmar(`${det}\n\nRemover mesmo assim?`, { titulo: 'Mudança retroativa na pontuação', perigo: true, rotuloConfirmar: 'Remover mesmo assim' }))) {
                    await apiService.pointsResetConfig({ userId, subdomain, store_id: storeId, confirm_retroactive: true });
                } else { throw e; }
            }
            addToast && addToast('Loja passou a usar a configuração default', 'success');
            load();
        } catch (e) {
            addToast && addToast(e.response?.data?.detail || 'Erro ao remover override', 'error');
        } finally { setSaving(false); }
    };

    if (!subdomain) return null;
    const usesDefault = !!storeId && data && !data.has_override;

    return (
        <div className="pe-points-config">
            <h3>⭐ Pontuação — {phaseId ? 'fase selecionada' : storeId ? 'loja selecionada' : 'ongoing (default da empresa)'}</h3>
            <PlacarAbreEm subdomain={subdomain} storeId={storeId} phaseId={phaseId} addToast={addToast} />
            {phaseId && (
                <p className="pe-pc-note">
                    Esta régua vale <strong>só para os atendimentos avaliados nesta fase</strong> e
                    <strong> substitui</strong> a da loja e a da empresa — não soma. Por isso os pontos dela
                    ficam <strong>fora do total do ongoing</strong> na Revisão de Pontos e aparecem no bloco
                    da fase: pontos de réguas diferentes não são comparáveis no mesmo ranking.
                </p>
            )}
            {loading ? <p>Carregando…</p> : (
                <>
                    {storeId && (
                        <p className="pe-pc-note">
                            {usesDefault
                                ? `Esta loja usa a configuração default (atend.: ${data?.default?.valid_attendance ?? 0}, hora: ${data?.default?.recording_hour ?? 0}). Salve abaixo para criar uma configuração específica da loja.`
                                : `Configuração específica desta loja. Default da empresa — atend.: ${data?.default?.valid_attendance ?? 0}, hora: ${data?.default?.recording_hour ?? 0}.`}
                        </p>
                    )}
                    <div className="pe-pc-row">
                        <label>Pontos por atendimento válido
                            <input type="number" step="1" value={va} onChange={e => setVa(e.target.value)} />
                        </label>
                        <label>Pontos por atendimento não-válido
                            <input type="number" step="1" value={inv} onChange={e => setInv(e.target.value)} />
                        </label>
                        <label>Pontos por hora de gravação
                            <input type="number" step="1" value={rh} onChange={e => setRh(e.target.value)} />
                        </label>
                    </div>
                    {!phaseId && (
                        <div className="pe-pc-vigencia">
                            <label>Vale a partir de
                                <input type="date" value={vf} min={dataBR(1)} onChange={e => setVf(e.target.value)} />
                            </label>
                            <small className="pe-pc-note">
                                Só é possível agendar a partir de <strong>amanhã</strong>. Os atendimentos e as horas de hoje e de
                                dias anteriores continuam com o valor que valia na época: mudar a pontuação <strong>não</strong>
                                reprecifica o passado. Pode trocar no meio de uma meta: a meta não depende do valor do ponto.
                            </small>
                            {(data?.proximas || []).length > 0 && (
                                <div className="pe-pc-agendadas">
                                    ⏳ <strong>Agendado:</strong>{' '}
                                    {data.proximas.map((p, i) => (
                                        <span key={`${p.metric}-${p.valid_from}-${i}`}>
                                            {i > 0 && '; '}{ROTULO_METRICA[p.metric] || p.metric} = <strong>{p.points_per_unit}</strong> a partir de {fmtDataBR(p.valid_from)}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {(data?.vigencias || []).filter((v) => v.status !== 'agendada').length > 1 && (
                                <details className="pe-pc-historico">
                                    <summary>Histórico de valores</summary>
                                    <ul>
                                        {data.vigencias.map((v, i) => (
                                            <li key={`${v.metric}-${v.valid_from}-${i}`} className={v.status === 'substituida' ? 'pe-pc-subst' : ''}>
                                                {ROTULO_METRICA[v.metric] || v.metric}: <strong>{v.points_per_unit}</strong>
                                                {' '}desde {v.valid_from === '1900-01-01' ? 'o início' : fmtDataBR(v.valid_from)}
                                                {v.store_id ? ' (loja)' : ''}
                                                {v.status === 'em_vigor' ? ' — em vigor' : v.status === 'agendada' ? ' — agendado' : ' — substituído'}
                                            </li>
                                        ))}
                                    </ul>
                                </details>
                            )}
                        </div>
                    )}
                    <div className="pe-cls-rules">
                        <h4>Classificações — o que cai em cada balde</h4>
                        {!clsRules ? (
                            <small className="pe-cls-hint">Carregando a régua da empresa…</small>
                        ) : (
                            <>
                                <div className="pe-cls-cols">
                                    <div>
                                        <span className="pe-cls-tag pe-cls-valid">Atendimento válido</span>
                                        <ul>
                                            {(clsRules.buckets?.valid || []).map((c) => <li key={c}><code>{c}</code></li>)}
                                            {!(clsRules.buckets?.valid || []).length && <li className="pe-cls-vazio">nenhuma</li>}
                                        </ul>
                                    </div>
                                    <div>
                                        <span className="pe-cls-tag pe-cls-invalid">Atendimento não-válido</span>
                                        <ul>
                                            {(clsRules.buckets?.invalid || []).map((c) => <li key={c}><code>{c}</code></li>)}
                                            {!(clsRules.buckets?.invalid || []).length && <li className="pe-cls-vazio">nenhuma</li>}
                                        </ul>
                                    </div>
                                    <div>
                                        <span className="pe-cls-tag pe-cls-none">Não conta</span>
                                        <ul>
                                            {(clsRules.buckets?.none || []).map((c) => <li key={c}><code>{c}</code></li>)}
                                            {!(clsRules.buckets?.none || []).length && <li className="pe-cls-vazio">nenhuma</li>}
                                        </ul>
                                    </div>
                                </div>
                                <small className="pe-cls-hint">
                                    {clsRules.catch_all && (
                                        <>Classificação fora desta lista conta como{' '}
                                        <strong>{clsRules.catch_all === 'valid' ? 'válido'
                                            : clsRules.catch_all === 'invalid' ? 'não-válido' : 'nada'}</strong>.{' '}</>
                                    )}
                                    {clsRules.configured
                                        ? <>Régua vigente desde <strong>{clsRules.valid_from}</strong>. Atendimento anterior a essa
                                           data segue a régua que valia no dia — mudar o mapa <strong>não</strong> reprecifica o passado.</>
                                        : <>Esta empresa ainda não tem régua própria e usa a lista padrão do sistema.</>}
                                    {' '}Somente leitura: alterar exige dev.
                                </small>
                                {clsRules.next && (
                                    <small className="pe-cls-hint pe-cls-agendada">
                                        ⏳ <strong>Muda em {clsRules.next.valid_from}</strong> — a partir dessa data
                                        contam como válido:{' '}
                                        {(clsRules.next.buckets?.valid || []).map((c) => <code key={c}>{c}</code>)
                                            .reduce((a, e) => a.length ? [...a, ', ', e] : [e], [])}
                                        {!(clsRules.next.buckets?.valid || []).length && 'nenhuma classificação'}.
                                        Atendimento gravado antes dessa data continua com a régua de hoje.
                                    </small>
                                )}
                            </>
                        )}
                    </div>
                    <div className="pe-pc-actions">
                        <button className="pe-btn pe-btn-primary" onClick={() => save()} disabled={saving}>
                            {saving ? 'Salvando…'
                                : phaseId ? 'Salvar config da fase'
                                : storeId ? 'Salvar config da loja'
                                : 'Salvar config do ongoing'}
                        </button>
                        {storeId && !usesDefault && (
                            <button className="pe-btn pe-btn-secondary" onClick={useDefault} disabled={saving}>
                                Usar default
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

// Conjunto curado de emojis p/ o picker (sem dependência externa). Salvo/retornado como emoji.
// A grade é só atalho: o campo "ou cole outro emoji" abaixo aceita QUALQUER emoji.
const EMOJI_SET = [
    // destaque e conquista
    '💡','⭐','🌟','✨','🎯','🏆','🥇','🥈','🥉','🏅','🎖️','🚀','📈','📊','✅','☑️','❤️','🧡','💛','💚','💙','💜','👍','👏','🙌','🤝','🔥','🎉','🎊','💯','💥','⚡','🌈',
    // dinheiro e venda
    '💰','💸','💵','💳','🧾','🏦','🪙','📉','🧮','🛒','🛍️','🎁','🏷️','📦','🧺','💎','🪪','🤑','📑',
    // casa e obra
    '🏠','🏡','🛋️','🛏️','🚿','🛁','🧴','🪑','🔧','🛠️','🧰','🔑','🚪','🧹','🔨','🧯','🪞','🪟','🔌','🪴','🌿',
    // veículos
    '🚗','🚙','🚘','🏎️','🛵','🏍️','🚚','⛽','🛞','🧭','🚦','🅿️','🚲',
    // ótica, olhos e água
    '👓','🕶️','🥽','👁️','👀','🔍','🔎','💧','💦','🫧','🌊','🧿','🔬','🧪','🩺',
    // moda e beleza
    '👗','👔','👕','👖','👟','👠','👜','👛','🎒','🧢','⌚','💍','💄','💅','🧼','🪥','🧽','✂️',
    // contato e comunicação
    '📞','☎️','💬','🗨️','🗣️','📣','📢','📱','💻','🖥️','🎧','🎤','📅','🗓️','🕒','⏱️','⏰','🔔','📄','📝','📋','📌','📎','✉️','📧',
    // pessoas e atendimento
    '👥','👤','🧑‍💼','🙂','😀','😄','😊','😍','🤩','🥳','😎','🤗','👋','🙏','💁','🧑‍🔧','🧑‍🍳','🧑‍⚕️','🫶',
    // saúde, comida e outros
    '💊','🩹','🩸','🦷','🏥','☕','🍎','🍕','🎂','🌞','🌙','☀️','⛅','🌸','🌻','🐾','🧸','🎮','🎵','📚','🎓','🧠','🛡️','⚙️','🔒','🔓','♻️','🆕','🆓','🔝','➕','➡️','❓','❗',
];

// Primeiro "caractere" visível do texto colado. Emoji pode ter vários code points (🕶️, 👨‍👩‍👧, 1️⃣),
// então corta por grafema quando o navegador tem Intl.Segmenter; sem ele, limita o tamanho.
function firstGrapheme(text) {
    const t = (text || '').trim();
    if (!t) return '';
    try {
        if (typeof Intl !== 'undefined' && Intl.Segmenter) {
            const it = new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(t)[Symbol.iterator]().next();
            return it.done ? '' : it.value.segment;
        }
    } catch (e) { /* cai no fallback */ }
    return t.slice(0, 16);
}

// Só aceita o que parece emoji (pictograma, bandeira, keycap), para não gravar letra solta no ícone.
function looksLikeEmoji(g) {
    if (!g) return false;
    try {
        if (new RegExp('\\p{Extended_Pictographic}|\\p{Regional_Indicator}', 'u').test(g)) return true;
    } catch (e) { return true; }                       // navegador sem \p{...}: não bloqueia
    return /[\u20E3\uFE0F]/.test(g);
}

function EmojiPicker({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const [custom, setCustom] = useState('');
    const [invalid, setInvalid] = useState(false);
    const onCustom = (raw) => {
        const g = firstGrapheme(raw);
        setCustom(g);
        if (!g) { setInvalid(false); return; }
        if (looksLikeEmoji(g)) { setInvalid(false); onChange(g); }
        else setInvalid(true);
    };
    return (
        <div className="pe-emoji-picker">
            <div className="pe-emoji-row">
                <button type="button" className="pe-emoji-trigger" onClick={() => setOpen(o => !o)}>
                    {value ? <span className="pe-emoji-current">{value}</span> : <span className="pe-emoji-ph">escolher ícone…</span>}
                    <span className="pe-emoji-caret">▾</span>
                </button>
                {value && (
                    <button type="button" className="pe-emoji-clear" title="Remover ícone"
                        onClick={() => { onChange(''); setCustom(''); setInvalid(false); }}>×</button>
                )}
            </div>
            <div className="pe-emoji-custom">
                <input type="text" className="pe-emoji-input" value={custom}
                    onChange={(e) => onCustom(e.target.value)}
                    onPaste={(e) => { e.preventDefault(); onCustom(e.clipboardData.getData('text')); }}
                    placeholder="ou cole qualquer emoji" aria-label="Colar outro emoji" autoComplete="off" />
                {invalid && <span className="pe-emoji-invalid">isso não parece um emoji</span>}
            </div>
            {open && (
                <div className="pe-emoji-grid">
                    {EMOJI_SET.map((e, i) => (
                        <button type="button" key={`${e}-${i}`}
                            className={`pe-emoji-item ${value === e ? 'selected' : ''}`}
                            onClick={() => { onChange(e); setCustom(''); setInvalid(false); setOpen(false); }}>{e}</button>
                    ))}
                </div>
            )}
        </div>
    );
}

function BehaviorModal({ initial, onClose, onSave, grupos = [], comHierarquia = false }) {
    const isEdit = !!initial;
    const [behaviorKey, setBehaviorKey] = useState(initial?.behavior_key || '');
    const [displayName, setDisplayName] = useState(initial?.display_name || '');
    const [maxPoints, setMaxPoints] = useState(initial?.max_points ?? 0);
    const [color, setColor] = useState(initial?.color || '#4caf50');
    const [icon, setIcon] = useState(initial?.icon || '');
    // grupos e ordem (feat/behavior-groups): só no escopo da empresa/loja; a fase ainda não os tem
    const [isGroup, setIsGroup] = useState(!!initial?.is_group);
    const [parentKey, setParentKey] = useState(initial?.parent_key || '');
    const [sortOrder, setSortOrder] = useState(initial?.sort_order ?? '');
    const slug = (behaviorKey || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    const canSave = slug && displayName.trim();
    const submit = () => {
        if (!canSave) return;
        onSave({
            behavior_key: slug,
            display_name: displayName.trim(),
            max_points: Number(maxPoints) || 0,
            color: color || null,
            icon: icon.trim() || null,
            ...(comHierarquia ? {
                is_group: isGroup,
                parent_key: isGroup ? null : (parentKey || null),
                sort_order: sortOrder === '' ? null : Number(sortOrder),
            } : {}),
        });
    };
    return (
        <div className="pe-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="pe-modal">
                <div className="pe-modal-header">
                    <h3>{isEdit ? 'Editar Comportamento' : 'Novo Comportamento'}</h3>
                    <button className="pe-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="pe-modal-body">
                    <div className="pe-form-group">
                        <label>Chave (behavior_key) *</label>
                        <input type="text" value={behaviorKey} disabled={isEdit}
                            onChange={e => setBehaviorKey(e.target.value)}
                            placeholder="ex: marca_propria" />
                        {!isEdit && slug && slug !== behaviorKey && (
                            <span className="pe-label-hint">será salvo como: {slug}</span>
                        )}
                    </div>
                    <div className="pe-form-group">
                        <label>Nome de exibição *</label>
                        <input type="text" value={displayName}
                            onChange={e => setDisplayName(e.target.value)} placeholder="ex: Marca própria" />
                    </div>
                    <div className="pe-form-group">
                        <label>Teto de pontos (max_points)</label>
                        <input type="number" step="1" value={maxPoints}
                            onChange={e => setMaxPoints(e.target.value)} />
                    </div>
                    <div className="pe-form-group" style={{ display: 'flex', gap: 16 }}>
                        <label style={{ flex: 1 }}>Cor
                            <input type="color" value={color || '#4caf50'}
                                onChange={e => setColor(e.target.value)} style={{ width: '100%', height: 34 }} />
                        </label>
                        <div className="pe-form-group" style={{ flex: 1, marginBottom: 0 }}>
                            <label>Ícone</label>
                            <EmojiPicker value={icon} onChange={setIcon} />
                        </div>
                    </div>
                    {comHierarquia && (
                        <>
                            <div className="pe-form-group">
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input type="checkbox" checked={isGroup} onChange={e => setIsGroup(e.target.checked)} />
                                    É um grupo (só reúne outros comportamentos; não recebe perguntas)
                                </label>
                            </div>
                            {!isGroup && (
                                <div className="pe-form-group">
                                    <label>Grupo</label>
                                    <select value={parentKey} onChange={e => setParentKey(e.target.value)}>
                                        <option value="">— nenhum (aparece sozinho) —</option>
                                        {grupos.filter(g => g.behavior_key !== slug).map(g => (
                                            <option key={g.behavior_key} value={g.behavior_key}>{g.display_name || g.behavior_key}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="pe-form-group">
                                <label>Ordem na lista</label>
                                <input type="number" step="1" min="1" value={sortOrder}
                                    onChange={e => setSortOrder(e.target.value)} placeholder="vazio = depois dos numerados" />
                                <span className="pe-label-hint">Dentro de um grupo, a ordem vale só entre os irmãos.</span>
                            </div>
                        </>
                    )}
                </div>
                <div className="pe-modal-footer">
                    <button className="pe-btn pe-btn-secondary" onClick={onClose}>Cancelar</button>
                    <button className="pe-btn pe-btn-primary" onClick={submit} disabled={!canSave}>Salvar</button>
                </div>
            </div>
        </div>
    );
}

/** Com `phaseId`, grava em `phase_behaviors` (§5.1) — o conjunto da fase SUBSTITUI o da loja na
 *  revisão do atendimento, e por isso é aqui que o CS define o que vale naquela fase. */
// Prompt de DETALHE do comportamento (aliado-awon awon.behavior.details, 26/10): quando o worker2 pontua o
// comportamento, este prompt extrai o detalhe (ex.: complementar_fez -> item oferecido, como, aceitou). Ligado ao
// comportamento pelo NOME do slug — sem cascata: um prompt por empresa. Editado aqui e escondido da lista de prompts.
const BEHAVIOR_DETAIL_PREFIX = 'awon_behavior_details_';
const isBehaviorDetailSlug = (slug) => /^(awon_)?behavior_details_/.test(slug || '');
// Prompt de detalhe do comportamento, um por pipeline (par de slugs, decisão 27/10): o AWON (worker2) usa
// awon_behavior_details_<chave>; o pipeline padrão (n8n -> API /behavior-details/extract) usa behavior_details_<chave>.
const DETALHE_ABAS = [
    { key: 'awon', label: 'AWON', prefix: BEHAVIOR_DETAIL_PREFIX,
      ajuda: 'O worker2 do AWON roda este prompt quando o comportamento pontua numa janela. O modelo recebe a janela do ' +
             'comportamento e a seguinte, e as ocorrências já identificadas (trecho, instante, cliente).' },
    { key: 'padrao', label: 'Pipeline padrão', prefix: 'behavior_details_',
      ajuda: 'O pipeline padrão (gravação normal do app e recortes avaliados pelo n8n) roda este prompt depois da ' +
             'avaliação. O modelo recebe o atendimento inteiro e a evidência que a avaliação registrou.' },
];

function BehaviorDetailPromptModal({ subdomain, behavior, onClose, addToast }) {
    const [aba, setAba] = useState('awon');
    const [porAba, setPorAba] = useState({});   // {awon|padrao: {loading, text, orig}}; orig null = ainda não existe
    const [saving, setSaving] = useState(false);
    const def = DETALHE_ABAS.find(a => a.key === aba);
    const slug = def.prefix + behavior.behavior_key;
    const cur = porAba[aba] || { loading: true, text: '', orig: null };
    const setCur = (patch) => setPorAba(prev => ({ ...prev, [aba]: { ...(prev[aba] || {}), ...patch } }));

    useEffect(() => {
        let vivo = true;
        DETALHE_ABAS.forEach(async (a) => {
            const sl = a.prefix + behavior.behavior_key;
            let st = { loading: false, text: '', orig: null };
            try {
                const active = await apiService.promptEditorResolveActivePrompt({ subdomain, slug: sl });
                if (active && active.active_prompt_id) {
                    const info = await apiService.promptEditorGetPromptData(subdomain, active.active_prompt_id);
                    st = { loading: false, text: active.active_content || '', orig: { ...info, content: active.active_content } };
                }
            } catch (e) {
                // sem prompt ainda (404): começa vazio
            }
            if (vivo) setPorAba(prev => ({ ...prev, [a.key]: st }));
        });
        return () => { vivo = false; };
    }, [subdomain, behavior.behavior_key]);

    const salvar = async () => {
        setSaving(true);
        try {
            await apiService.promptEditorUpsertPrompt({ subdomain, content: cur.text, slug, store_id: null,
                original_id: cur.orig?.original_id || null, ia_model: null });
            addToast && addToast(`${def.label}: ${cur.orig ? 'nova versão do prompt de detalhe salva' : 'prompt de detalhe criado'}`,
                'success');
            onClose();
        } catch (e) {
            addToast && addToast(`Erro ao salvar: ${e.message}`, 'error');
        } finally { setSaving(false); }
    };

    const awonText = porAba.awon?.text || '';
    return (
        <div className="pe-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="pe-modal" style={{ maxWidth: 900, width: '95%' }}>
                <div className="pe-modal-header">
                    <h3>📝 Prompt de detalhe — {behavior.icon ? behavior.icon + ' ' : ''}{behavior.display_name}</h3>
                    <button className="pe-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="pe-modal-body">
                    <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                        {DETALHE_ABAS.map(a => (
                            <button key={a.key} className={`pe-btn ${aba === a.key ? 'pe-btn-blue' : 'pe-btn-secondary'}`}
                                onClick={() => setAba(a.key)}>
                                {a.label}{porAba[a.key] && !porAba[a.key].loading && !porAba[a.key].orig ? ' (vazio)' : ''}
                            </button>
                        ))}
                    </div>
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 0 }}>
                        {def.ajuda} Peça a resposta em JSON no formato <code>{'{"ocorrencias": [...]}'}</code>; os campos
                        que você pedir aparecem na Revisão de pontos. Sem prompt, nada é extraído neste pipeline.
                        <br />Slug: <code>{slug}</code>{cur.orig?.version ? ` · versão ${cur.orig.version}` : cur.orig ? '' : ' · ainda não criado'}
                    </p>
                    {cur.loading ? <p>Carregando…</p> : (<>
                        {aba === 'padrao' && awonText.trim() && (
                            <button className="pe-btn pe-btn-secondary" style={{ marginBottom: 8 }}
                                onClick={() => setCur({ text: awonText })}>
                                Copiar do AWON (ajuste depois: no pipeline padrão não há vendedor1 nem janela seguinte)
                            </button>
                        )}
                        <textarea value={cur.text} onChange={e => setCur({ text: e.target.value })} rows={22}
                            style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.82rem' }}
                            placeholder="Escreva o prompt de detalhe deste comportamento…" />
                    </>)}
                </div>
                <div className="pe-modal-footer">
                    <button className="pe-btn pe-btn-secondary" onClick={onClose}>Cancelar</button>
                    <button className="pe-btn pe-btn-blue"
                        disabled={cur.loading || saving || !cur.text.trim() || (cur.orig && cur.text === cur.orig.content)}
                        onClick={salvar}>{saving ? 'Salvando…' : cur.orig ? `Salvar nova versão (${def.label})` : `Criar prompt (${def.label})`}</button>
                </div>
            </div>
        </div>
    );
}

function BehaviorsPanel({ subdomain, storeId, phaseId, userId, addToast, onChanged }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(undefined); // undefined=fechado, null=novo, obj=editar
    const [detalhe, setDetalhe] = useState(null);      // comportamento cujo prompt de detalhe está aberto
    const [removendo, setRemovendo] = useState(null);  // {bk, item} do comportamento a remover
    const [removendoOcupado, setRemovendoOcupado] = useState(false);

    const load = useCallback(async () => {
        if (!subdomain) return;
        setLoading(true);
        try {
            // ESCOPO DE FASE LÊ A RÉGUA DA FASE. O bug que isto corrige: o save já gravava em
            // `phase_behaviors`, mas a leitura sempre vinha do ongoing — então o comportamento
            // recém-cadastrado na fase salvava com sucesso e NUNCA aparecia na tela.
            const d = phaseId
                ? await apiService.phaseRulerList({ subdomain, phase_id: phaseId })
                : await apiService.pointsGetBehaviors({ userId, subdomain, store_id: storeId || undefined });
            setData(d);
        } catch (e) {
            addToast && addToast('Erro ao carregar comportamentos', 'error');
        } finally { setLoading(false); }
    }, [subdomain, storeId, phaseId, userId, addToast]);
    useEffect(() => { load(); }, [load]);

    // Na fase, a lista são os comportamentos DELA; os herdados vêm marcados e sem ações, para o CS
    // ver o que a fase substitui e o que ela apenas herda da loja/global.
    const daFase = phaseId ? (data?.data?.behaviors || []) : [];
    const herdados = phaseId ? (data?.data?.behaviors_inherited || []) : [];
    const list = phaseId
        ? [...daFase.map((b) => ({ ...b, _daFase: true })),
           ...herdados.filter((h) => !daFase.some((b) => b.behavior_key === h.behavior_key))
                      .map((h) => ({ ...h, _herdado: true }))]
        : (storeId ? (data?.effective || []) : (data?.default || []));

    // Grupo e ordem valem na empresa, na loja E na fase (o grupo é POR FASE: marca_propria é grupo numa
    // fase e folha em outra). Na fase, só os comportamentos DELA entram na hierarquia; os herdados
    // da loja/global ficam planos no fim. A API já entrega a lista na ordem que o app recebe; aqui só
    // se coloca cada filho logo abaixo do seu grupo.
    const comHierarquia = true;
    const baseHier = phaseId ? list.filter(b => b._daFase) : list;
    const caudaPlana = phaseId ? list.filter(b => !b._daFase) : [];
    const gruposDaLista = baseHier.filter(b => b.is_group);
    const exibidos = (() => {
        const chaves = new Set(gruposDaLista.map(g => g.behavior_key));
        const filhosDe = (k) => baseHier.filter(b => b.parent_key === k);
        const topo = baseHier.filter(b => !b.parent_key || !chaves.has(b.parent_key));
        return [
            ...topo.flatMap(b => [{ ...b, _nivel: 0 }, ...(b.is_group ? filhosDe(b.behavior_key).map(f => ({ ...f, _nivel: 1 })) : [])]),
            ...caudaPlana.map(b => ({ ...b, _nivel: 0 })),
        ];
    })();

    // Reordenar renumera 1..N os IRMÃOS (mesmo grupo) e manda o item inteiro (a API reinsere a linha
    // inteira, então nome, teto, cor e ícone vão junto; grupo e is_group não vão e ficam como estão).
    // Na visão da empresa ou da fase: na loja isto criaria um override por irmão sem que ninguém peça.
    const podeReordenar = !!phaseId || !storeId;
    const mover = async (b, delta) => {
        if (b._herdado) return;   // herdado da loja/global não tem ordem na fase
        const irmaos = exibidos.filter(x => !x._herdado && (x.parent_key || null) === (b.parent_key || null) && x._nivel === b._nivel);
        const i = irmaos.findIndex(x => x.behavior_key === b.behavior_key);
        const j = i + delta;
        if (i < 0 || j < 0 || j >= irmaos.length) return;
        const nova = irmaos.slice();
        [nova[i], nova[j]] = [nova[j], nova[i]];
        try {
            if (phaseId) {
                for (const [n, x] of nova.entries()) {
                    await apiService.phaseBehaviorSave({
                        subdomain, phase_id: phaseId,
                        behavior_key: x.behavior_key, display_name: x.display_name, max_points: x.max_points,
                        color: x.color, icon: x.icon, sort_order: n + 1,
                    });
                }
            } else {
                await apiService.pointsSaveBehaviors({
                    userId, subdomain, store_id: undefined,
                    items: nova.map((x, n) => ({
                        behavior_key: x.behavior_key, display_name: x.display_name, max_points: x.max_points,
                        color: x.color, icon: x.icon, sort_order: n + 1,
                    })),
                });
            }
            await load();
            onChanged && onChanged();
        } catch (e) {
            addToast && addToast(e.response?.data?.detail || 'Erro ao reordenar', 'error');
        }
    };

    const save = async (item) => {
        try {
            if (phaseId) {
                await apiService.phaseBehaviorSave({
                    subdomain, phase_id: phaseId,
                    behavior_key: item.behavior_key, display_name: item.display_name,
                    max_points: item.max_points, color: item.color, icon: item.icon,
                    parent_key: item.parent_key, is_group: item.is_group, sort_order: item.sort_order,
                });
            } else {
                await apiService.pointsSaveBehaviors({ userId, subdomain, store_id: storeId || undefined, items: [item] });
            }
            addToast && addToast(phaseId ? 'Comportamento da fase salvo' : 'Comportamento salvo', 'success');
            setEditing(undefined);
            await load();
            onChanged && onChanged();
        } catch (e) {
            addToast && addToast(e.response?.data?.detail || 'Erro ao salvar comportamento', 'error');
        }
    };

    const pedirRemocao = (bk, item) => {
        // NUNCA cair no delete do ongoing quando o escopo é fase: aquele, com store_id nulo, apaga o
        // comportamento GLOBAL da empresa. Era o segundo bug deste painel, e o pior: silencioso e
        // destrutivo. Item herdado não tem o que remover na fase.
        if (phaseId && item?._herdado) {
            addToast && addToast('Este comportamento é herdado da loja/global — remova no escopo dele.', 'info');
            return;
        }
        setRemovendo({ bk, item });
    };

    const del = async () => {
        const { bk, item } = removendo;
        setRemovendoOcupado(true);
        try {
            if (phaseId) {
                await apiService.phaseBehaviorDelete({ subdomain, id: item.id });
            } else {
                await apiService.pointsDeleteBehavior({ userId, subdomain, store_id: storeId || undefined, behavior_key: bk });
            }
            addToast && addToast('Comportamento removido', 'success');
            setRemovendo(null);
            await load();
            onChanged && onChanged();
        } catch (e) {
            addToast && addToast(e.response?.data?.detail || 'Erro ao remover comportamento', 'error');
        } finally { setRemovendoOcupado(false); }
    };

    if (!subdomain) return null;
    return (
        <div className="pe-behaviors">
            <div className="pe-questions-toolbar">
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    {phaseId ? 'Comportamentos da fase (substituem os da loja/global)'
                        : storeId ? 'Comportamentos efetivos da loja (default + overrides)'
                            : 'Comportamentos default da empresa'}
                </span>
                <button className="pe-btn pe-btn-blue" style={{ marginLeft: 'auto' }} onClick={() => setEditing(null)}>
                    ➕ Comportamento
                </button>
            </div>
            {loading ? <p>Carregando…</p> : list.length === 0 ? (
                <div className="pe-empty"><div className="pe-empty-icon">🎯</div><p>Nenhum comportamento cadastrado.</p></div>
            ) : (
                <table className="pe-table" style={{ width: '100%' }}>
                    <thead><tr>
                        <th>Ícone</th><th>Nome</th><th>Chave</th><th>Teto</th><th>Cor</th>
                        {comHierarquia && <th>Ordem</th>}
                        {storeId && <th>Override?</th>}<th></th>
                    </tr></thead>
                    <tbody>
                        {exibidos.map(b => (
                            <tr key={b.behavior_key}>
                                <td>{b.icon || '—'}</td>
                                <td style={b._nivel ? { paddingLeft: 28 } : undefined}>
                                    {b._nivel ? '↳ ' : ''}{b.display_name}
                                    {b.is_group && <span className="pe-tag-herdado" title="Reúne outros comportamentos; não recebe perguntas">grupo</span>}
                                    {/* Herdado aparece marcado e sem ações: o CS precisa ver o que a fase
                                        substitui e o que ela só herda, senão editar ali parece funcionar
                                        e grava no lugar errado. */}
                                    {b._herdado && <span className="pe-tag-herdado" title="Vem da loja/global; a fase não sobrescreve">herdado</span>}
                                </td>
                                <td><code>{b.behavior_key}</code></td>
                                <td>{b.max_points}</td>
                                <td><span style={{ display: 'inline-block', width: 16, height: 16, borderRadius: 3, background: b.color || 'transparent', border: '1px solid #ccc' }} title={b.color} /></td>
                                {comHierarquia && <td>{b.sort_order ?? '—'}</td>}
                                {storeId && <td>{b.has_override ? '✅' : '— (default)'}</td>}
                                <td style={{ whiteSpace: 'nowrap' }}>
                                    {podeReordenar && (
                                        <>
                                            <button className="pe-btn pe-btn-secondary" style={{ padding: '3px 8px', fontSize: '0.78rem', marginRight: 4 }} title="Subir" onClick={() => mover(b, -1)}>↑</button>
                                            <button className="pe-btn pe-btn-secondary" style={{ padding: '3px 8px', fontSize: '0.78rem', marginRight: 6 }} title="Descer" onClick={() => mover(b, 1)}>↓</button>
                                        </>
                                    )}
                                    <button className="pe-btn pe-btn-blue" style={{ padding: '3px 10px', fontSize: '0.78rem' }}
                                        onClick={() => setEditing(b._herdado ? { ...b, id: undefined, parent_key: null, is_group: false, sort_order: null } : b)}>✏️</button>
                                    {!b.is_group && !storeId && !phaseId && (
                                        <button className="pe-btn pe-btn-secondary" style={{ padding: '3px 10px', fontSize: '0.78rem', marginLeft: 6 }}
                                            title="Prompt de detalhe: extrai o que foi feito quando este comportamento pontua"
                                            onClick={() => setDetalhe(b)}>📝 Detalhe</button>
                                    )}
                                    {(phaseId ? b._daFase : (!storeId || b.has_override)) && (
                                        <button className="pe-btn pe-btn-danger" style={{ padding: '3px 10px', fontSize: '0.78rem', marginLeft: 6 }} onClick={() => pedirRemocao(b.behavior_key, b)}>🗑️</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {editing !== undefined && (
                <BehaviorModal initial={editing} onClose={() => setEditing(undefined)} onSave={save}
                    grupos={gruposDaLista} comHierarquia={comHierarquia} />
            )}
            {detalhe && (
                <BehaviorDetailPromptModal subdomain={subdomain} behavior={detalhe} onClose={() => setDetalhe(null)}
                    addToast={addToast} />
            )}

            {/* Confirmação: o mesmo diálogo da Saída da turma. A consequência muda com o escopo e é o
                que a pessoa precisa ler ANTES de confirmar — na fase ela volta a herdar, na loja o
                override desaparece, no global o comportamento deixa de existir para a empresa toda. */}
            {removendo && (
                <Dialogo
                    titulo={`Remover “${removendo.item?.display_name || removendo.bk}”?`}
                    perigo
                    ocupado={removendoOcupado}
                    rotuloConfirmar="Remover"
                    rotuloOcupado="Removendo…"
                    onCancelar={() => setRemovendo(null)}
                    onConfirmar={del}
                >
                    <p className="dlg-efeito">
                        {phaseId
                            ? <>A fase deixa de ter este comportamento e <strong>volta a herdar</strong> o da loja/global.</>
                            : storeId
                                ? <>Esta loja deixa de ter override e <strong>volta a valer o default</strong> da empresa.</>
                                : <>O comportamento deixa de existir <strong>para a empresa toda</strong>.</>}
                    </p>
                    <p className="dlg-nota">
                        Os atendimentos já avaliados <strong>continuam valendo</strong> o que valeram — a remoção
                        afeta só as avaliações futuras.
                    </p>
                </Dialogo>
            )}
        </div>
    );
}

// ===== SCORING TYPE EDITOR =====
function ScoringPromptEditor({ resolvedData, promptInfo, selectors, addToast, onRefresh }) {
    const [activeTab, setActiveTab] = useState('prompt');
    const resolvedContent = resolvedData?.prompt_content || '';

    // Prompt de scoring (4 - Salesperson Performance Evaluation): conteúdo é referência
    // (hardcoded no n8n), exibido somente leitura — sem edição de texto.

    // --- QUESTIONS TAB ---
    const [questions, setQuestions] = useState(() => normalizeQuestions(resolvedData?.questions));
    const originalQuestionsRef = useRef(normalizeQuestions(resolvedData?.questions));
    const [showAddModal, setShowAddModal] = useState(false);
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [sendingQuestions, setSendingQuestions] = useState(false);

    // --- MODELO DE IA por-prompt (override; gravado em prompts.metadata igual aos demais) ---
    const initialModel = resolvedData?.prompt_ia_model || '';
    const [selectedModel, setSelectedModel] = useState(initialModel);
    const [iaModels, setIaModels] = useState([]);
    const [savingModel, setSavingModel] = useState(false);
    useEffect(() => { apiService.getIaModels().then((m) => setIaModels(m || [])); }, []);
    const modelLabelOf = (key) => (iaModels.find((m) => m.model_key === key)?.label) || key;
    // Modelo/effort por pergunta: só o 4-Eval (evaluation_prompt) roda cada pergunta no modelo dela.
    const iaPorPergunta = selectors.slug === 'evaluation_prompt'
        ? { models: iaModels, herdadoKey: resolvedData?.prompt_ia_model || resolvedData?.company_default_ia_model || '' }
        : null;
    const mostraAvisos = (res) => (res?.avisos || []).forEach((a) => addToast(a, 'info'));
    const handleSaveModel = async () => {
        setSavingModel(true);
        try {
            const res = await apiService.promptEditorChangeScoringPromptQuestions({
                subdomain: selectors.subdomain,
                slug: selectors.slug,
                store_id: selectors.storeId,
                phase_id: selectors.phaseId,
                questions: [],
                ia_model: selectedModel || null,
            });
            addToast('Modelo de IA do prompt atualizado!', 'success');
            mostraAvisos(res);
            onRefresh();
        } catch (err) {
            addToast(`Erro ao salvar modelo: ${err.message}`, 'error');
        } finally {
            setSavingModel(false);
        }
    };

    // --- COMPORTAMENTOS (dropdown das perguntas + aba) ---
    const behUserId = (() => {
        try { return JSON.parse(localStorage.getItem('aliado_auth'))?.user?.user_id; } catch (e) { return null; }
    })();
    const [behaviors, setBehaviors] = useState([]);   // o que VALE no escopo ativo (fase > loja > empresa)
    const loadBehaviors = useCallback(async () => {
        if (!selectors.subdomain) return;
        try {
            // MESMO ESCOPO DO PAINEL. Isto alimenta duas coisas: o contador da aba e o dropdown de
            // comportamento das perguntas. Lendo sempre o ongoing, uma empresa que só tem
            // comportamentos por fase (o caso do leroymerlin) mostrava "Comportamentos (0)" e o
            // dropdown vazio — impossível amarrar pergunta a comportamento no escopo de fase.
            // `effective` só é preenchido quando há loja; sem loja o que vale é `default`.
            if (selectors.phaseId) {
                const r = await apiService.phaseRulerList({ subdomain: selectors.subdomain, phase_id: selectors.phaseId });
                const daFase = r?.data?.behaviors || [];
                const herdados = r?.data?.behaviors_inherited || [];
                setBehaviors([
                    ...daFase,
                    ...herdados.filter((h) => !daFase.some((b) => b.behavior_key === h.behavior_key)),
                ].filter((b) => !b.is_group));   // grupo não recebe pergunta
                return;
            }
            const d = await apiService.pointsGetBehaviors({
                userId: behUserId, subdomain: selectors.subdomain, store_id: selectors.storeId || undefined,
            });
            // grupo não recebe pergunta: fica fora do select da pergunta
            setBehaviors(((selectors.storeId ? d?.effective : d?.default) || []).filter((b) => !b.is_group));
        } catch (e) { /* silencioso: dropdown fica vazio */ }
    }, [selectors.subdomain, selectors.storeId, selectors.phaseId, behUserId]);
    useEffect(() => { loadBehaviors(); }, [loadBehaviors]);

    const handleResetQuestions = () => {
        setQuestions(normalizeQuestions(resolvedData?.questions));
        originalQuestionsRef.current = normalizeQuestions(resolvedData?.questions);
    };

    // --- VOLTAR AO GLOBAL (apaga o override desta loja) ---
    const useGlobal = useUseGlobalPrompt({ selectors, addToast, onRefresh, hasQuestions: true });

    const handleQuestionChange = (q, field, value) => {
        setQuestions(prev => prev.map(item => {
            if (item._key !== q._key) return item;
            const origQ = originalQuestionsRef.current.find(o => o._key === item._key);
            const updated = { ...item, [field]: value };
            if (!item._isNew && origQ) {
                const changed =
                    updated.description !== origQ.description ||
                    updated.condition !== origQ.condition ||
                    updated.score_prompt !== origQ.score_prompt ||
                    updated.category !== origQ.category ||
                    updated.weight !== origQ.weight ||
                    !!updated.earn_points !== !!origQ.earn_points ||
                    (updated.points || 0) !== (origQ.points || 0) ||
                    (updated.behavior_key || '') !== (origQ.behavior_key || '') ||
                    (updated.ia_model || '') !== (origQ.ia_model || '') ||
                    (updated.effort || '') !== (origQ.effort || '');
                updated._modified = changed;
            }
            return updated;
        }));
    };

    const handleDeleteQuestion = (q) => {
        if (q._isNew) {
            setQuestions(prev => prev.filter(item => item._key !== q._key));
        } else {
            setQuestions(prev => prev.map(item =>
                item._key === q._key ? { ...item, _deleted: true, _modified: true } : item
            ));
        }
    };

    const handleAddQuestion = (newQ) => {
        const key = _newQuestionKey--;
        setQuestions(prev => [...prev, {
            _key: key,
            _originalId: null,
            _currentId: null,
            _isNew: true,
            _modified: true,
            _deleted: false,
            description: newQ.description,
            condition: newQ.condition || '',
            score_prompt: newQ.score_prompt,
            category: newQ.category || '',
            weight: newQ.weight || 1,
            earn_points: !!newQ.earn_points,
            points: newQ.points || 0,
            behavior_key: newQ.behavior_key || '',
            ia_model: '',
            effort: '',
        }]);
    };

    const buildQuestionActions = () => {
        const actions = [];
        const originals = originalQuestionsRef.current;

        // Removed
        originals.forEach(orig => {
            const cur = questions.find(c => c._key === orig._key);
            if (!cur || cur._deleted) {
                actions.push({
                    update_type: 'remove',
                    original_id: orig._originalId,
                    current_question_id: orig._currentId,
                });
            }
        });

        // Added
        questions.filter(c => c._isNew && !c._deleted).forEach(q => {
            actions.push({
                update_type: 'insert',
                question: q.description,
                description: q.description,
                condition: q.condition?.trim() || null,
                score_prompt: q.score_prompt?.trim() || null,
                category: q.category || null,
                weight: q.weight,
                earn_points: !!q.earn_points,
                points: q.earn_points ? (q.points || 0) : 0,
                behavior_key: q.behavior_key || null,
                // a rota grava a pergunta do zero: sem estes dois, o override se perderia
                ia_model: q.ia_model || null,
                effort: q.effort || null,
            });
        });

        // Modified
        questions.filter(c => !c._isNew && !c._deleted && c._modified).forEach(q => {
            actions.push({
                update_type: 'update',
                original_id: q._originalId,
                current_question_id: q._currentId,
                question: q.description,
                description: q.description,
                condition: q.condition?.trim() || null,
                score_prompt: q.score_prompt?.trim() || null,
                category: q.category || null,
                weight: q.weight,
                earn_points: !!q.earn_points,
                points: q.earn_points ? (q.points || 0) : 0,
                behavior_key: q.behavior_key || null,
                // a rota grava a pergunta do zero: sem estes dois, o override se perderia
                ia_model: q.ia_model || null,
                effort: q.effort || null,
            });
        });

        return actions;
    };

    const handleConfirmSendQuestions = async () => {
        setSendingQuestions(true);
        try {
            const actions = buildQuestionActions();
            // sem ia_model: a API mantém o modelo do prompt (até 21/10/2026 este envio o zerava)
            const res = await apiService.promptEditorChangeScoringPromptQuestions({
                subdomain: selectors.subdomain,
                slug: selectors.slug,
                store_id: selectors.storeId,
                phase_id: selectors.phaseId,
                questions: actions,
            });
            addToast('Perguntas atualizadas com sucesso!', 'success');
            mostraAvisos(res);
            setShowSummaryModal(false);
            onRefresh();
        } catch (err) {
            addToast(`Erro ao atualizar perguntas: ${err.message}`, 'error');
        } finally {
            setSendingQuestions(false);
        }
    };

    const activeQuestions = questions.filter(q => !q._deleted);

    return (
        <div>
            {/* Origem do prompt: no topo, valendo para todas as abas (antes só existia na aba Perguntas) */}
            <PromptOriginBadge
                resolvedData={resolvedData}
                storeId={selectors.storeId}
                onUseGlobal={useGlobal.run}
                usingGlobalBusy={useGlobal.busy}
            />
            {useGlobal.modal}

            {/* O painel de Pontuação NÃO mora mais aqui — subiu para o nível da aba. Ele ficava
                dentro deste editor, que só renderiza para o slug de scoring, e o slug de scoring é
                bloqueado em fase do tipo `leveling` (§3.1). Resultado: não havia como configurar a
                pontuação de uma fase de Nivelamento pela tela, embora a régua dela EXISTA e seja a
                que paga os pontos dos vendedores que estão nela. Preço de atendimento não tem
                relação com qual prompt a fase usa — a trava é do prompt, não da pontuação. */}

            {/* Tabs */}
            <div className="pe-tabs">
                <button
                    className={`pe-tab ${activeTab === 'prompt' ? 'active' : ''}`}
                    onClick={() => setActiveTab('prompt')}
                >
                    📝 Texto do Prompt
                </button>
                <button
                    className={`pe-tab ${activeTab === 'questions' ? 'active' : ''}`}
                    onClick={() => setActiveTab('questions')}
                >
                    ❓ Perguntas ({activeQuestions.length})
                </button>
                <button
                    className={`pe-tab ${activeTab === 'behaviors' ? 'active' : ''}`}
                    onClick={() => setActiveTab('behaviors')}
                >
                    🎯 Comportamentos ({behaviors.length})
                </button>
            </div>

            {/* PROMPT TEXT TAB */}
            {activeTab === 'prompt' && (
                <>
                    <div className="pe-info-bar">
                        <span className="pe-prompt-id-badge">
                            <span className="badge-label">Prompt ID</span>
                            #{resolvedData?.prompt_id}
                        </span>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                            original_id: {promptInfo?.original_id}
                        </span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem' }}>
                            <span className="badge-label">Modelo IA</span>
                            <select
                                value={selectedModel}
                                onChange={(e) => setSelectedModel(e.target.value)}
                                className="form-input"
                                style={{ padding: '4px 8px', width: 'auto' }}
                                title="Modelo de IA para este prompt (vazio = padrão da empresa)"
                            >
                                <option value="">Padrão da empresa{resolvedData?.company_default_ia_model ? ` (${modelLabelOf(resolvedData.company_default_ia_model)})` : ''}</option>
                                {modelosDoPrompt(iaModels, selectors.slug).map((m) => (
                                    <option key={m.model_key} value={m.model_key}>{m.label}</option>
                                ))}
                            </select>
                        </label>
                        <button
                            className="pe-btn pe-btn-primary"
                            onClick={handleSaveModel}
                            disabled={savingModel || selectedModel === initialModel}
                            style={{ marginLeft: 'auto' }}
                        >
                            {savingModel ? 'Salvando...' : '💾 Salvar modelo'}
                        </button>
                    </div>
                    <PromptTestRunner
                        subdomain={selectors.subdomain}
                        slug={selectors.slug}
                        promptId={resolvedData?.prompt_id}
                        storeId={selectors.storeId}
                        addToast={addToast}
                    />
                    {/* Scoring (4 - Salesperson Performance Evaluation): conteúdo é referência,
                        hardcoded no n8n. Mostra só o prompt atual (somente leitura), largura total. */}
                    <div className="pe-panels pe-panels-single">
                        <div className="pe-panel">
                            <div className="pe-panel-header">🔒 Prompt Atual (somente leitura)</div>
                            <textarea disabled value={resolvedContent} />
                        </div>
                    </div>
                </>
            )}

            {/* QUESTIONS TAB */}
            {activeTab === 'questions' && (
                <>
                    <div className="pe-info-bar">
                        <span className="pe-prompt-id-badge">
                            <span className="badge-label">Scoring Prompt ID</span>
                            #{resolvedData?.prompt_id}
                        </span>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                            original_id: {promptInfo?.original_id}
                        </span>
                    </div>
                    <div className="pe-questions-toolbar">
                        <button className="pe-btn pe-btn-danger" onClick={handleResetQuestions}>
                            ↩ Resetar Perguntas
                        </button>
                        <button className="pe-btn pe-btn-blue" onClick={() => setShowAddModal(true)}>
                            ➕ Adicionar Pergunta
                        </button>
                        <button
                            className="pe-btn pe-btn-success"
                            onClick={() => {
                                const invalid = activeQuestions.find(
                                    q => q.earn_points && !(Number.isInteger(Number(q.points)) && String(q.points).trim() !== '')
                                );
                                if (invalid) {
                                    addToast(`Pergunta "${(invalid.description || '').slice(0, 40)}…" gera pontos mas o campo de pontos está vazio ou inválido.`, 'error');
                                    return;
                                }
                                const effortRuim = iaPorPergunta && activeQuestions.find(
                                    q => q.effort && !effortsDaPergunta(q, iaPorPergunta.models, iaPorPergunta.herdadoKey).includes(q.effort)
                                );
                                if (effortRuim) {
                                    addToast(`Pergunta "${(effortRuim.description || '').slice(0, 40)}…": o effort "${effortRuim.effort}" não vale para o modelo dela.`, 'error');
                                    return;
                                }
                                setShowSummaryModal(true);
                            }}
                        >
                            📤 Enviar Atualização de Perguntas
                        </button>
                    </div>
                    <div className="pe-questions-list">
                        {activeQuestions.length === 0 ? (
                            <div className="pe-empty">
                                <div className="pe-empty-icon">❓</div>
                                <p>Nenhuma pergunta. Adicione uma nova.</p>
                            </div>
                        ) : (
                            activeQuestions.map((q, idx) => (
                                <QuestionCard
                                    key={q._key}
                                    q={q}
                                    index={idx}
                                    behaviors={behaviors}
                                    onChange={handleQuestionChange}
                                    onDelete={handleDeleteQuestion}
                                    ia={iaPorPergunta}
                                />
                            ))
                        )}
                    </div>

                    {showAddModal && (
                        <AddQuestionModal
                            onClose={() => setShowAddModal(false)}
                            onAdd={handleAddQuestion}
                        />
                    )}

                    {showSummaryModal && (
                        <QuestionsSummaryModal
                            original={originalQuestionsRef.current}
                            current={questions}
                            onClose={() => setShowSummaryModal(false)}
                            onConfirm={handleConfirmSendQuestions}
                            loading={sendingQuestions}
                        />
                    )}
                </>
            )}

            {/* BEHAVIORS TAB */}
            {activeTab === 'behaviors' && (
                <BehaviorsPanel
                    subdomain={selectors.subdomain}
                    storeId={selectors.storeId}
                    phaseId={selectors.phaseId}
                    userId={behUserId}
                    addToast={addToast}
                    onChanged={loadBehaviors}
                />
            )}
        </div>
    );
}

// ===== SIMPLE (null type) EDITOR =====
// Taxonomias por-tenant que o n8n e o worker2 do AWON injetam no prompt e usam para validar
// a saída da IA. Os dois leem só o default da empresa (store_id NULL), então o admin edita
// sempre a lista da empresa, mesmo com uma loja selecionada.
const CATEGORY_KINDS = {
    objections: {
        tab: '🏷️ Categorias de objeção',
        titulo: 'categorias de objeção',
        novo: 'Nova Categoria de Objeção',
        editar: 'Editar Categoria de Objeção',
        botao: '➕ Categoria',
        campo: 'objection_type',
        slugExemplo: 'preco_alto',
        nomeExemplo: 'Preço alto',
        descExemplo: 'Quando esta categoria se aplica e como contorná-la.',
        prompts: ['objections', 'extract_data_audit', 'awon_objections'],
        get: (a) => apiService.pointsGetObjectionCategories(a),
        save: (a) => apiService.pointsSaveObjectionCategories(a),
        del: (a) => apiService.pointsDeleteObjectionCategory(a),
    },
    lost_sales_reason: {
        tab: '🏷️ Motivos de não venda',
        titulo: 'motivos de não venda',
        novo: 'Novo Motivo de Não Venda',
        editar: 'Editar Motivo de Não Venda',
        botao: '➕ Motivo',
        campo: 'lost_sales_reason_slug',
        slugExemplo: 'prazo_entrega',
        nomeExemplo: 'Prazo de entrega',
        descExemplo: 'Quando este motivo se aplica.',
        prompts: ['lost_sales_reason', 'extract_data_audit', 'awon_lost_sales_reason'],
        get: (a) => apiService.pointsGetLostSalesReasonCategories(a),
        save: (a) => apiService.pointsSaveLostSalesReasonCategories(a),
        del: (a) => apiService.pointsDeleteLostSalesReasonCategory(a),
    },
};

// slug do prompt -> taxonomias que ele recebe injetadas. Os awon_* são as cópias do worker2
// (aliado-awon: src/awon/behavior/extractors.py, CATEGORY_TABLE) e leem as mesmas tabelas.
const SLUG_CATEGORY_KINDS = {
    objections: ['objections'],
    lost_sales_reason: ['lost_sales_reason'],
    extract_data_audit: ['objections', 'lost_sales_reason'],
    awon_objections: ['objections'],
    awon_lost_sales_reason: ['lost_sales_reason'],
};

function CategoryModal({ kind, initial, onClose, onSave }) {
    const cfg = CATEGORY_KINDS[kind];
    const isEdit = !!initial;
    const [slugInput, setSlugInput] = useState(initial?.slug || '');
    const [displayName, setDisplayName] = useState(initial?.display_name || '');
    const [description, setDescription] = useState(initial?.description || '');
    // slug padronizado: minúsculas, espaços -> '_', só [a-z0-9_]
    const slug = (slugInput || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    const canSave = slug && displayName.trim();
    const submit = () => {
        if (!canSave) return;
        onSave({
            slug,
            display_name: displayName.trim(),
            description: description.trim() || null,
        });
    };
    return (
        <div className="pe-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="pe-modal">
                <div className="pe-modal-header">
                    <h3>{isEdit ? cfg.editar : cfg.novo}</h3>
                    <button className="pe-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="pe-modal-body">
                    <div className="pe-form-group">
                        <label>Slug *</label>
                        <input type="text" value={slugInput} disabled={isEdit}
                            onChange={e => setSlugInput(e.target.value)}
                            placeholder={`ex: ${cfg.slugExemplo}`} />
                        <span className="pe-label-hint">
                            Sem espaços — use apenas <code>_</code> como separador.
                            {!isEdit && slug && slug !== slugInput && <> Será salvo como: <b>{slug}</b></>}
                        </span>
                    </div>
                    <div className="pe-form-group">
                        <label>Nome de exibição *</label>
                        <input type="text" value={displayName}
                            onChange={e => setDisplayName(e.target.value)} placeholder={`ex: ${cfg.nomeExemplo}`} />
                    </div>
                    <div className="pe-form-group">
                        <label>Descrição</label>
                        <textarea value={description} rows={4}
                            onChange={e => setDescription(e.target.value)}
                            placeholder={cfg.descExemplo} />
                    </div>
                </div>
                <div className="pe-modal-footer">
                    <button className="pe-btn pe-btn-secondary" onClick={onClose}>Cancelar</button>
                    <button className="pe-btn pe-btn-primary" onClick={submit} disabled={!canSave}>Salvar</button>
                </div>
            </div>
        </div>
    );
}

function CategoriesPanel({ kind, subdomain, storeId, userId, addToast }) {
    const cfg = CATEGORY_KINDS[kind];
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(undefined); // undefined=fechado, null=novo, obj=editar
    const [removendo, setRemovendo] = useState(null);  // item a remover
    const [removendoOcupado, setRemovendoOcupado] = useState(false);

    const load = useCallback(async () => {
        if (!subdomain) return;
        setLoading(true);
        try {
            setData(await cfg.get({ userId, subdomain }));
        } catch (e) {
            addToast && addToast(`Erro ao carregar ${cfg.titulo}`, 'error');
        } finally { setLoading(false); }
    }, [cfg, subdomain, userId, addToast]);
    useEffect(() => { load(); }, [load]);

    const list = data?.default || [];
    const outrosPrompts = cfg.prompts.length > 1 ? cfg.prompts : null;

    const save = async (item) => {
        try {
            await cfg.save({ userId, subdomain, items: [item] });
            addToast && addToast('Salvo', 'success');
            setEditing(undefined);
            await load();
        } catch (e) {
            addToast && addToast(e.response?.data?.detail || 'Erro ao salvar', 'error');
        }
    };

    const del = async () => {
        setRemovendoOcupado(true);
        try {
            await cfg.del({ userId, subdomain, slug: removendo.slug });
            addToast && addToast('Removido', 'success');
            setRemovendo(null);
            await load();
        } catch (e) {
            addToast && addToast(e.response?.data?.detail || 'Erro ao remover', 'error');
        } finally { setRemovendoOcupado(false); }
    };

    if (!subdomain) return null;
    return (
        <div className="pe-behaviors">
            <div className="pe-info-note" style={{ margin: '0 0 12px', padding: '10px 12px', background: 'var(--bg-subtle, #f5f7fa)', borderLeft: '3px solid #4caf50', borderRadius: 4, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                ℹ️ Estes são os {cfg.titulo} (<code>{cfg.campo}</code>) deste cliente: a lista é injetada no prompt e
                é o que valida a saída da IA. Se o texto do prompt também citar a lista, mantenha os dois coerentes.
                {outrosPrompts && <> A mesma lista é usada pelos prompts {outrosPrompts.map((p, i) => <React.Fragment key={p}>{i > 0 && (i === outrosPrompts.length - 1 ? ' e ' : ', ')}<code>{p}</code></React.Fragment>)}.</>}
                {storeId && <> <b>Vale para a empresa toda</b>, não só para a loja selecionada.</>}
            </div>
            <div className="pe-questions-toolbar">
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    {list.length} {cfg.titulo} da empresa
                </span>
                <button className="pe-btn pe-btn-blue" style={{ marginLeft: 'auto' }} onClick={() => setEditing(null)}>
                    {cfg.botao}
                </button>
            </div>
            {loading ? <p>Carregando…</p> : list.length === 0 ? (
                <div className="pe-empty"><div className="pe-empty-icon">🏷️</div><p>Nada cadastrado.</p></div>
            ) : (
                <table className="pe-table" style={{ width: '100%' }}>
                    <thead><tr>
                        <th>Nome</th><th>Slug</th><th>Descrição</th><th></th>
                    </tr></thead>
                    <tbody>
                        {list.map(c => (
                            <tr key={c.slug}>
                                <td>{c.display_name}</td>
                                <td><code>{c.slug}</code></td>
                                <td style={{ maxWidth: 420, whiteSpace: 'normal' }}>{c.description || '—'}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                    <button className="pe-btn pe-btn-blue" style={{ padding: '3px 10px', fontSize: '0.78rem' }} onClick={() => setEditing(c)}>✏️</button>
                                    <button className="pe-btn pe-btn-danger" style={{ padding: '3px 10px', fontSize: '0.78rem', marginLeft: 6 }} onClick={() => setRemovendo(c)}>🗑️</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {editing !== undefined && (
                <CategoryModal kind={kind} initial={editing} onClose={() => setEditing(undefined)} onSave={save} />
            )}

            {removendo && (
                <Dialogo
                    titulo={`Remover “${removendo.display_name || removendo.slug}”?`}
                    perigo
                    ocupado={removendoOcupado}
                    rotuloConfirmar="Remover"
                    rotuloOcupado="Removendo…"
                    onCancelar={() => setRemovendo(null)}
                    onConfirmar={del}
                >
                    <p className="dlg-efeito">
                        Deixa de existir <strong>para a empresa toda</strong>
                        {outrosPrompts && <> e em todos os prompts que usam esta lista</>}.
                    </p>
                    <p className="dlg-nota">
                        Se ainda estiver citado no <strong>texto do prompt</strong>, a IA pode continuar devolvendo
                        esse slug e a saída passa a ser reprovada pelo validador — revise o prompt também.
                    </p>
                </Dialogo>
            )}
        </div>
    );
}

function SimplePromptEditor({ resolvedData, promptInfo, selectors, addToast, onRefresh }) {
    const resolvedContent = resolvedData?.active_content || '';
    const [editedText, setEditedText] = useState(resolvedContent);
    const [saving, setSaving] = useState(false);
    const categoryKinds = SLUG_CATEGORY_KINDS[selectors.slug] || [];
    const isPlaybook = selectors.slug === 'playbook';
    const [activeTabRaw, setActiveTab] = useState('prompt');
    // trocar de slug com a aba de uma taxonomia aberta cai de volta no texto do prompt
    const activeTab = categoryKinds.includes(activeTabRaw) ? activeTabRaw : 'prompt';
    const useGlobal = useUseGlobalPrompt({ selectors, addToast, onRefresh, hasQuestions: false });
    const catUserId = (() => { try { return JSON.parse(localStorage.getItem('aliado_auth'))?.user?.user_id; } catch (e) { return undefined; } })();

    // Modelo de IA por-prompt (override): '' => usa o padrão da empresa (cascata)
    const initialModel = resolvedData?.prompt_ia_model || '';
    const [selectedModel, setSelectedModel] = useState(initialModel);
    const [iaModels, setIaModels] = useState([]);
    useEffect(() => { apiService.getIaModels().then((m) => setIaModels(m || [])); }, []);
    const modelLabelOf = (key) => (iaModels.find((m) => m.model_key === key)?.label) || key;
    const modelDirty = selectedModel !== initialModel;

    const handleRevert = () => { setEditedText(resolvedContent); setSelectedModel(initialModel); };

    const handleSave = async () => {
        setSaving(true);
        try {
            await apiService.promptEditorUpsertPrompt({
                subdomain: selectors.subdomain,
                content: editedText,
                slug: selectors.slug,
                store_id: selectors.storeId,
                phase_id: selectors.phaseId,
                original_id: promptInfo.original_id,
                ia_model: isPlaybook ? null : (selectedModel || null),
            });
            addToast('Prompt atualizado com sucesso!', 'success');
            onRefresh();
        } catch (err) {
            addToast(`Erro ao salvar prompt: ${err.message}`, 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <PromptOriginBadge
                resolvedData={resolvedData}
                storeId={selectors.storeId}
                onUseGlobal={useGlobal.run}
                usingGlobalBusy={useGlobal.busy}
            />
            {useGlobal.modal}
            {isPlaybook && (
                <p className="pe-slug-hint" style={{ margin: '4px 0 16px' }}>
                    ℹ️ Este playbook é usado apenas no prompt <strong>"5.1- Feedback Longo"</strong>.
                </p>
            )}
            {categoryKinds.length > 0 && (
                <div className="pe-tabs">
                    <button className={`pe-tab ${activeTab === 'prompt' ? 'active' : ''}`} onClick={() => setActiveTab('prompt')}>📝 Texto do Prompt</button>
                    {categoryKinds.map(k => (
                        <button key={k} className={`pe-tab ${activeTab === k ? 'active' : ''}`} onClick={() => setActiveTab(k)}>{CATEGORY_KINDS[k].tab}</button>
                    ))}
                </div>
            )}
            {activeTab === 'prompt' && (<>
            <div className="pe-info-bar">
                <span className="pe-prompt-id-badge">
                    <span className="badge-label">Prompt ID</span>
                    #{resolvedData?.active_prompt_id}
                </span>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    original_id: {promptInfo?.original_id}
                </span>
                {!isPlaybook && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem' }}>
                        <span className="badge-label">Modelo IA</span>
                        <select
                            value={selectedModel}
                            onChange={(e) => setSelectedModel(e.target.value)}
                            className="form-input"
                            style={{ padding: '4px 8px', width: 'auto' }}
                            title="Modelo de IA para este prompt (vazio = padrão da empresa)"
                        >
                            <option value="">Padrão da empresa{resolvedData?.company_default_ia_model ? ` (${modelLabelOf(resolvedData.company_default_ia_model)})` : ''}</option>
                            {modelosDoPrompt(iaModels, selectors.slug).map((m) => (
                                <option key={m.model_key} value={m.model_key}>{m.label}</option>
                            ))}
                        </select>
                    </label>
                )}
                <div className="pe-info-bar-actions">
                    <button
                        className="pe-btn pe-btn-secondary"
                        onClick={handleRevert}
                        title="Reverter alterações"
                    >
                        ↩ Reverter
                    </button>
                    <button
                        className="pe-btn pe-btn-primary"
                        onClick={handleSave}
                        disabled={saving || (editedText === resolvedContent && !modelDirty)}
                    >
                        {saving ? 'Salvando...' : (isPlaybook ? '💾 Atualizar Playbook' : '💾 Atualizar Prompt')}
                    </button>
                </div>
            </div>
            <PromptTestRunner
                subdomain={selectors.subdomain}
                slug={selectors.slug}
                promptId={resolvedData?.active_prompt_id}
                storeId={selectors.storeId}
                addToast={addToast}
            />
            <div className="pe-panels">
                <div className="pe-panel">
                    <div className="pe-panel-header">🔒 Prompt Atual (somente leitura)</div>
                    <textarea disabled value={resolvedContent} />
                </div>
                <div className="pe-panel">
                    <div className="pe-panel-header">✏️ Editar Prompt</div>
                    <textarea
                        value={editedText}
                        onChange={e => setEditedText(e.target.value)}
                        placeholder="Digite o novo conteúdo do prompt..."
                    />
                </div>
            </div>
            </>)}
            {activeTab !== 'prompt' && (
                <CategoriesPanel
                    key={activeTab}
                    kind={activeTab}
                    subdomain={selectors.subdomain}
                    storeId={selectors.storeId}
                    userId={catUserId}
                    addToast={addToast}
                />
            )}
        </div>
    );
}

// ===== MAIN PAGE =====
/**
 * `scope` é OPCIONAL e vem do ScopeProvider quando esta tela roda como aba de Configurações
 * (§9.3). Sem ele — na rota antiga /prompt-editor — a tela mantém os próprios selects e se
 * comporta exatamente como antes.
 *
 * Com escopo de FASE, os dois resolvers passam a receber phase_id e devolvem pela cascata
 * fase > loja > global (§3.1). A ESCRITA por fase ainda não está ligada aqui: salvar continua
 * gravando no escopo de loja/empresa, e por isso a tela avisa quando o escopo é fase.
 */
export default function PromptEditor({ scope } = {}) {
    const escopoExterno = !!scope?.subdomain;
    const escopoFase = escopoExterno && scope.kind === 'phase';
    const { toasts, addToast } = useToasts();

    // Selectors state
    const [companies, setCompanies] = useState([]);
    const [stores, setStores] = useState([]);
    const [slugs, setSlugs] = useState([]);
    // Restrição tipo de fase x slug (§3.1). A API já devolvia isto pronto em
    // `list-phase-prompt-assignments` (`exclusive_slugs`, com `allowed_for_this_phase`) e a tela
    // não usava: o CS descobria a regra só ao salvar, com um 400.
    const [faseRestricoes, setFaseRestricoes] = useState(null);

    const [selectedCompanyLocal, setSelectedCompany] = useState('');
    const [selectedStoreIdLocal, setSelectedStoreId] = useState('');
    // Com escopo externo ele MANDA: a tela não pode divergir do que o seletor mostra.
    const selectedCompany = escopoExterno ? scope.subdomain : selectedCompanyLocal;
    const selectedStoreId = escopoExterno
        ? (scope.kind === 'store' ? String(scope.store_id) : '')
        : selectedStoreIdLocal;
    const scopePhaseId = escopoFase ? scope.phase_id : null;
    const [selectedSlug, setSelectedSlug] = useState('');

    // Loading states
    const [loadingCompanies, setLoadingCompanies] = useState(true);
    const [loadingStores, setLoadingStores] = useState(false);
    const [loadingSlugs, setLoadingSlugs] = useState(false);
    const [loadingEditor, setLoadingEditor] = useState(false);

    // Resolved prompt data
    const [resolvedData, setResolvedData] = useState(null);
    const [promptInfo, setPromptInfo] = useState(null);
    const [refreshCount, setRefreshCount] = useState(0);

    // Modais globais (playbook / prompts padrão / cópia entre lojas)
    const [showPlaybookModal, setShowPlaybookModal] = useState(false);
    const [showImportTextModal, setShowImportTextModal] = useState(false);
    const [showDefaultsModal, setShowDefaultsModal] = useState(false);
    const [showCopyStoreModal, setShowCopyStoreModal] = useState(false);

    const [exporting, setExporting] = useState(false);

    const handleExportPrompts = async () => {
        setExporting(true);
        try {
            const { filename } = await apiService.promptEditorExportPrompts({
                subdomain: selectedCompany,
                store_id: selectedStoreId || null,
            });
            addToast(`Prompts exportados: ${filename}`, 'success');
        } catch (err) {
            addToast(err.message, 'error');
        } finally {
            setExporting(false);
        }
    };

    const reloadSlugs = async () => {
        setSelectedSlug('');
        setResolvedData(null);
        setPromptInfo(null);
        try {
            const data = await apiService.promptEditorGetSlugs(selectedCompany);
            setSlugs(Array.isArray(data) ? data : []);
        } catch (err) {
            addToast(`Erro ao recarregar slugs: ${err.message}`, 'error');
        }
    };

    // Load companies on mount
    useEffect(() => {
        (async () => {
            try {
                const data = await apiService.promptEditorGetCompanies();
                setCompanies(Array.isArray(data) ? data : []);
            } catch (err) {
                addToast(`Erro ao carregar empresas: ${err.message}`, 'error');
            } finally {
                setLoadingCompanies(false);
            }
        })();
    }, [addToast]);

    // Load stores when company changes
    useEffect(() => {
        if (!selectedCompany) {
            setStores([]);
            setSelectedStoreId('');
            setSlugs([]);
            setSelectedSlug('');
            setResolvedData(null);
            setPromptInfo(null);
            return;
        }
        setLoadingStores(true);
        setStores([]);
        setSelectedStoreId('');
        setSlugs([]);
        setSelectedSlug('');
        setResolvedData(null);
        setPromptInfo(null);
        (async () => {
            try {
                const data = await apiService.promptEditorGetStores(selectedCompany);
                setStores(Array.isArray(data) ? data : []);
            } catch (err) {
                addToast(`Erro ao carregar lojas: ${err.message}`, 'error');
            } finally {
                setLoadingStores(false);
            }
        })();
    }, [selectedCompany, addToast]);

    // Slugs que a FASE selecionada pode configurar. Sem fase no escopo não há restrição nenhuma.
    useEffect(() => {
        setFaseRestricoes(null);
        if (!selectedCompany || !scopePhaseId) return;
        let vivo = true;
        (async () => {
            try {
                const r = await apiService.phasePromptAssignmentsList({
                    subdomain: selectedCompany, phase_id: scopePhaseId,
                });
                if (vivo) setFaseRestricoes(r?.data || r || null);
            } catch (e) {
                // Silencioso de propósito: sem isto a tela perde o AVISO, não a função — e a API
                // continua barrando a gravação errada.
            }
        })();
        return () => { vivo = false; };
    }, [selectedCompany, scopePhaseId]);

    /** Devolve `{tipos: [...]}` quando a fase atual NÃO pode configurar este slug; null se pode. */
    const restricaoDoSlug = useCallback((slug) => {
        const linhas = (faseRestricoes?.exclusive_slugs || []).filter((x) => x.slug === slug);
        if (!linhas.length) return null;                                   // slug livre para qualquer tipo
        if (linhas.some((x) => x.allowed_for_this_phase)) return null;     // esta fase é de um tipo permitido
        return { tipos: [...new Set(linhas.map((x) => x.phase_type_name))] };
    }, [faseRestricoes]);

    const bloqueioDaFase = restricaoDoSlug(selectedSlug);

    // Load slugs when company changes (store is optional)
    useEffect(() => {
        if (!selectedCompany) return;
        setLoadingSlugs(true);
        setSlugs([]);
        setSelectedSlug('');
        setResolvedData(null);
        setPromptInfo(null);
        (async () => {
            try {
                const data = await apiService.promptEditorGetSlugs(selectedCompany);
                setSlugs(Array.isArray(data) ? data : []);
            } catch (err) {
                addToast(`Erro ao carregar slugs: ${err.message}`, 'error');
            } finally {
                setLoadingSlugs(false);
            }
        })();
    }, [selectedCompany, addToast]);

    // Reset prompt when store changes
    useEffect(() => {
        if (selectedSlug && selectedCompany) {
            loadPrompt(selectedCompany, selectedSlug, selectedStoreId || null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStoreId, scopePhaseId]);

    const loadPrompt = async (subdomain, slug, storeId) => {
        setLoadingEditor(true);
        setResolvedData(null);
        setPromptInfo(null);
        let scoring = null;
        let active = null;
        let promptId = null;

        try {
            scoring = await apiService.promptEditorResolveScoringPrompt({
                subdomain,
                slug,
                store_id: storeId || null,
                phase_id: scopePhaseId,
            });
        } catch (_e) {
            // Not a scoring prompt — fall through to active prompt
        }

        if (!scoring || !scoring.prompt_id) {
            // Try resolve-active-prompt for null-type prompts
            try {
                active = await apiService.promptEditorResolveActivePrompt({
                    subdomain,
                    slug,
                    store_id: storeId || null,
                    phase_id: scopePhaseId,
                });
            } catch (err2) {
                addToast(`Erro ao carregar prompt: ${err2.message}`, 'error');
                setLoadingEditor(false);
                return;
            }
            if (!active || !active.active_prompt_id) {
                addToast('Nenhum prompt encontrado para esta combinação.', 'info');
                setLoadingEditor(false);
                return;
            }
            promptId = active.active_prompt_id;
            setResolvedData(active);
        } else {
            promptId = scoring.prompt_id;
            setResolvedData(scoring);
        }

        try {
            const info = await apiService.promptEditorGetPromptData(subdomain, promptId);
            setPromptInfo(info);

            // The resolve-scoring-prompt endpoint returns data for ANY prompt type (not just scoring),
            // but uses { prompt_id, prompt_content } keys. If this is actually a null-type prompt,
            // normalize the shape to { active_prompt_id, active_content } so SimplePromptEditor works.
            if (info?.prompt_type !== 'scoring') {
                setResolvedData(prev => {
                    if (!prev) return prev;
                    // Already in active-prompt shape
                    if (prev.active_prompt_id) return prev;
                    // Came from scoring-resolve endpoint — remap field names (preserva campos de IA)
                    return {
                        active_prompt_id: prev.prompt_id,
                        active_content: prev.prompt_content,
                        original_id: prev.original_id,
                        is_pinned: prev.is_pinned,
                        assignment_store_id: prev.assignment_store_id,
                        prompt_ia_model: prev.prompt_ia_model,
                        ia_model: prev.ia_model,
                        ia_provider: prev.ia_provider,
                        ia_model_id: prev.ia_model_id,
                        company_default_ia_model: prev.company_default_ia_model,
                    };
                });
            }
            setRefreshCount(c => c + 1);
        } catch (err) {
            addToast(`Erro ao buscar dados do prompt: ${err.message}`, 'error');
        } finally {
            setLoadingEditor(false);
        }
    };

    const handleSlugChange = async (slug) => {
        setSelectedSlug(slug);
        setResolvedData(null);
        setPromptInfo(null);
        if (!slug || !selectedCompany) return;
        await loadPrompt(selectedCompany, slug, selectedStoreId || null);
    };

    const currentSelectors = {
        subdomain: selectedCompany,
        slug: selectedSlug,
        storeId: selectedStoreId ? Number(selectedStoreId) : null,
        // nome da loja p/ o modal de "Usar prompt GLOBAL" identificar o alvo
        storeName: stores.find(s => String(s.id) === String(selectedStoreId))?.display_name || null,
        slugLabel: slugs.find(s => s.slug === selectedSlug)?.name || selectedSlug,
        phaseId: scopePhaseId,
    };

    const isScoring = promptInfo?.prompt_type === 'scoring';

    return (
        <div className="prompt-editor">
            {!escopoExterno && <h1>🖊️ Editor de Prompts</h1>}

            {/* SELECTORS */}
            <div className="pe-selectors">
                {!escopoExterno && (
                <div className="pe-selector-group">
                    <label>Empresa</label>
                    <select
                        value={selectedCompany}
                        onChange={e => setSelectedCompany(e.target.value)}
                        disabled={loadingCompanies}
                    >
                        <option value="">{loadingCompanies ? 'Carregando...' : '— Selecione a empresa —'}</option>
                        {companies.map(c => (
                            <option key={c.subdomain} value={c.subdomain}>{c.name}</option>
                        ))}
                    </select>
                </div>
                )}

                {!escopoExterno && (
                <div className="pe-selector-group">
                    <label>Loja (opcional)</label>
                    <select
                        value={selectedStoreId}
                        onChange={e => setSelectedStoreId(e.target.value)}
                        disabled={!selectedCompany || loadingStores}
                    >
                        <option value="">{loadingStores ? 'Carregando...' : '— Nenhuma loja —'}</option>
                        {stores.map(s => (
                            <option key={s.id} value={s.id}>{s.display_name}</option>
                        ))}
                    </select>
                </div>
                )}
            </div>

            {/* Pontuação vem ANTES do slug de propósito: são dois eixos independentes — o ESCOPO
                (empresa / loja / fase) determina a régua de pontos do atendimento, e o SLUG
                determina qual prompt está sendo editado. Enquanto o painel morava depois (e, pior,
                dentro do editor de scoring), configurar pontos parecia depender de escolher um
                prompt — e em fase de nivelamento, onde aquele slug é bloqueado, virava impossível. */}
            <PointsConfigPanel
                subdomain={selectedCompany}
                storeId={selectedStoreId ? Number(selectedStoreId) : null}
                phaseId={scopePhaseId}
                addToast={addToast}
            />

            <div className="pe-selectors">
                <div className="pe-selector-group">
                    <label>Slug do Prompt/Playbook</label>
                    <select
                        value={selectedSlug}
                        onChange={e => handleSlugChange(e.target.value)}
                        disabled={!selectedCompany || loadingSlugs}
                    >
                        <option value="">{loadingSlugs ? 'Carregando...' : '— Selecione o slug —'}</option>
                        {orderPromptSlugs(slugs.filter(s => !isBehaviorDetailSlug(s.slug))).map(s => {
                            const r = restricaoDoSlug(s.slug);
                            return (
                                <option key={s.slug} value={s.slug} disabled={!!r}>
                                    {promptOptionLabel(s)}
                                    {r ? ` — indisponível nesta fase (só em fase de ${r.tipos.join(' ou ')})` : ''}
                                </option>
                            );
                        })}
                    </select>
                </div>
            </div>

            <p className="pe-slug-hint">
                ℹ️ O prompt <strong>"4 - Salesperson Performance Evaluation"</strong> é onde se configuram as
                <strong> perguntas que pontuam</strong>. A pontuação por atendimento válido / não-válido / hora
                de gravação fica no painel acima e <strong>não depende do slug</strong> — ela é do escopo
                selecionado.
            </p>

            {/* AÇÕES GLOBAIS (independem de slug/prompt) */}
            <div className="pe-global-actions">
                <button
                    className="pe-btn pe-btn-blue"
                    disabled={!selectedCompany}
                    title={!selectedCompany ? 'Selecione uma empresa primeiro' : 'Gerar todos os prompts a partir de um playbook (substitui os prompts atuais)'}
                    onClick={() => setShowPlaybookModal(true)}
                >
                    📚 Criar Prompts pelo Playbook
                </button>
                <button
                    className="pe-btn pe-btn-secondary"
                    disabled={!selectedCompany}
                    title={!selectedCompany ? 'Selecione uma empresa primeiro'
                        : selectedStoreId ? 'Importar o texto do arquivo como o playbook da loja selecionada'
                        : 'Importar o texto do arquivo como o playbook global da empresa'}
                    onClick={() => setShowImportTextModal(true)}
                >
                    📥 Importar Texto do Playbook
                </button>
                <button
                    className="pe-btn pe-btn-secondary"
                    disabled={!selectedCompany}
                    title={!selectedCompany ? 'Selecione uma empresa primeiro' : 'Copiar prompts default de outra empresa (somente empresas novas)'}
                    onClick={() => setShowDefaultsModal(true)}
                >
                    🧬 Gerar Prompts Padrão
                </button>
                <button
                    className="pe-btn pe-btn-secondary"
                    disabled={!selectedCompany || exporting}
                    title={!selectedCompany ? 'Selecione uma empresa primeiro'
                        : selectedStoreId ? 'Exportar os prompts da loja selecionada para Excel'
                        : 'Exportar os prompts default da empresa para Excel'}
                    onClick={handleExportPrompts}
                >
                    {exporting ? '⏳ Exportando…' : '📤 Exportar Prompts'}
                </button>
                {selectedStoreId && (
                    <button
                        className="pe-btn pe-btn-secondary"
                        title="Substituir os prompts desta loja pelos de outra loja"
                        onClick={() => setShowCopyStoreModal(true)}
                    >
                        📋 Copiar Prompts de Outra Loja
                    </button>
                )}
                {!selectedCompany && (
                    <span className="pe-label-hint">Selecione uma empresa para habilitar as ações.</span>
                )}
            </div>

            {/* EDITOR AREA */}
            {loadingEditor && (
                <div className="pe-loading">
                    <div className="pe-spinner" />
                    Carregando prompt...
                </div>
            )}

            {!loadingEditor && !resolvedData && selectedSlug && (
                <div className="pe-empty">
                    <div className="pe-empty-icon">📭</div>
                    <p>Nenhum prompt encontrado para esta combinação.</p>
                </div>
            )}

            {!loadingEditor && !selectedSlug && (
                <div className="pe-empty">
                    <div className="pe-empty-icon">🖊️</div>
                    <p>Selecione uma empresa e um slug de prompt para começar a editar.</p>
                </div>
            )}

            {/* A fase escolhida não pode configurar este prompt (§3.1). O editor NÃO é mostrado: o
                que ele carregaria é o prompt da loja/global (o braço da fase não resolve nada), e
                editar ali dá a impressão de estar configurando a fase — até o 400 na gravação. */}
            {!loadingEditor && selectedSlug && bloqueioDaFase && (
                <div className="pe-bloqueio">
                    <h3>🔒 Este prompt não é configurável nesta fase</h3>

                    <p>
                        Você está no escopo da fase <strong>{faseRestricoes?.phase?.name || 'selecionada'}</strong>,
                        que é do tipo <strong>{faseRestricoes?.phase?.phase_type_name || '—'}</strong>. O prompt{' '}
                        <strong>{slugs.find((x) => x.slug === selectedSlug)?.name || selectedSlug}</strong> só pode ser
                        configurado por fase do tipo <strong>{bloqueioDaFase.tipos.join(' ou ')}</strong>.
                    </p>

                    <p>
                        <strong>Por que a regra existe:</strong> cada tipo de fase tem um papel na jornada, e o
                        conjunto de prompts que faz sentido nele é diferente. Uma fase de <em>Nivelamento</em> avalia o
                        atendimento com nota; uma de <em>Comportamentos</em> avalia os comportamentos que dão ponto.
                        Deixar qualquer fase sobrescrever qualquer prompt faria o vendedor ser avaliado por um critério
                        que não é o da fase dele — e a diferença só apareceria semanas depois, no placar.
                    </p>

                    <p><strong>O que fazer, conforme a sua intenção:</strong></p>
                    <ul>
                        <li>
                            <strong>Quero mudar este prompt para quem está numa fase de {bloqueioDaFase.tipos.join(' ou ')}:</strong>{' '}
                            troque o escopo, no seletor acima, para uma fase desse tipo. Em{' '}
                            <em>Programas e Fases</em> você vê o tipo de cada fase de cada programa.
                        </li>
                        <li>
                            <strong>Quero mudar para todo mundo da empresa (ou de uma loja):</strong> selecione{' '}
                            <em>Padrão (ongoing)</em> ou a loja no seletor de escopo. Vale para quem está em fase
                            também, sempre que a fase não tiver um prompt próprio.
                        </li>
                        <li>
                            <strong>A fase é que está com o tipo errado:</strong> corrija o tipo dela em{' '}
                            <em>Programas e Fases</em>. Atenção: se alguém já percorreu a fase, a estrutura fica
                            travada — nesse caso o caminho é criar uma fase nova com o tipo certo.
                        </li>
                    </ul>

                    <p className="pe-bloqueio-nota">
                        Enquanto este escopo estiver selecionado, o editor fica oculto de propósito: o texto que
                        apareceria aqui é o da loja/empresa, não o da fase, e salvar seria recusado pela API.
                    </p>
                </div>
            )}

            {!loadingEditor && resolvedData && promptInfo && !bloqueioDaFase && (
                isScoring ? (
                    <ScoringPromptEditor
                        key={`${selectedCompany}-${selectedSlug}-${selectedStoreId}-${refreshCount}`}
                        resolvedData={resolvedData}
                        promptInfo={promptInfo}
                        selectors={currentSelectors}
                        addToast={addToast}
                        onRefresh={() => loadPrompt(selectedCompany, selectedSlug, selectedStoreId || null)}
                    />
                ) : (
                    <SimplePromptEditor
                        key={`${selectedCompany}-${selectedSlug}-${selectedStoreId}-${refreshCount}`}
                        resolvedData={resolvedData}
                        promptInfo={promptInfo}
                        selectors={currentSelectors}
                        addToast={addToast}
                        onRefresh={() => loadPrompt(selectedCompany, selectedSlug, selectedStoreId || null)}
                    />
                )
            )}

            {showPlaybookModal && selectedCompany && (
                <PlaybookModal
                    subdomain={selectedCompany}
                    onClose={() => setShowPlaybookModal(false)}
                    onDone={reloadSlugs}
                    addToast={addToast}
                />
            )}

            {showImportTextModal && selectedCompany && (
                <ImportPlaybookTextModal
                    subdomain={selectedCompany}
                    storeId={selectedStoreId || null}
                    storeName={stores.find(s => String(s.id) === String(selectedStoreId))?.display_name || null}
                    onClose={() => setShowImportTextModal(false)}
                    onDone={async () => {
                        // Atualiza a lista de slugs (o `playbook` pode ter sido recém-criado)
                        // e recarrega o prompt aberto para refletir o texto importado.
                        try {
                            const data = await apiService.promptEditorGetSlugs(selectedCompany);
                            setSlugs(Array.isArray(data) ? data : []);
                        } catch (_e) { /* silencioso */ }
                        if (selectedSlug) {
                            loadPrompt(selectedCompany, selectedSlug, selectedStoreId || null);
                        }
                    }}
                    addToast={addToast}
                />
            )}

            {showDefaultsModal && selectedCompany && (
                <DefaultPromptsModal
                    subdomain={selectedCompany}
                    onClose={() => setShowDefaultsModal(false)}
                    onDone={reloadSlugs}
                    addToast={addToast}
                />
            )}

            {showCopyStoreModal && selectedCompany && selectedStoreId && (
                <CopyStorePromptsModal
                    subdomain={selectedCompany}
                    stores={stores}
                    targetStoreId={selectedStoreId}
                    onClose={() => setShowCopyStoreModal(false)}
                    onDone={() => {
                        if (selectedSlug) loadPrompt(selectedCompany, selectedSlug, selectedStoreId || null);
                    }}
                    addToast={addToast}
                />
            )}

            <ToastContainer toasts={toasts} />
        </div>
    );
}
